// FactoryInventory.tsx - الإصدار الكامل والمتوافق مع الراوتس
import React, { useState, useEffect, useCallback, useMemo, useReducer } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, Plus, X, Eye, Edit, AlertCircle, Minus, Search, ChevronDown, 
  ChefHat, Users, Filter, RefreshCw, Clock, CheckCircle, AlertTriangle,
  TrendingUp, BarChart, Download, Upload, Grid, List, Building,
  Loader2, ArrowRight, ArrowLeft, Shield, Ban, CheckCheck
} from 'lucide-react';
import { 
  factoryInventoryAPI, 
  factoryOrdersAPI, 
  productsAPI, 
  chefsAPI, 
  departmentAPI,
  isValidObjectId 
} from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { ProductSearchInput } from './NewOrder';
import { useNotifications } from '../contexts/NotificationContext';
import { Modal } from '../components/UI/Modal';
import { Button } from '../components/UI/Button';
import { CustomDropdown } from '../components/UI/CustomDropdown';

// ========== الأنواع والواجهات ==========
enum InventoryStatus {
  LOW = 'low',
  NORMAL = 'normal',
  HIGH = 'high',
}

interface FactoryInventoryItem {
  id: string;
  _id: string;
  product: {
    _id: string;
    name: string;
    nameEn: string;
    displayName: string;
    code: string;
    unit: string;
    unitEn: string;
    displayUnit: string;
    department: {
      _id: string;
      name: string;
      nameEn: string;
      displayName: string;
    } | null;
    price: number;
    minStockLevel: number;
    maxStockLevel: number;
  } | null;
  currentStock: number;
  pendingReturnStock: number;
  pendingShipStock: number;
  damagedStock: number;
  minStockLevel: number;
  maxStockLevel: number;
  availableStock: number;
  stockStatus: InventoryStatus;
  displayStockStatus: string;
  createdBy: any;
  updatedBy: any;
  createdAt: string;
  updatedAt: string;
}

interface ProductionItem {
  product: string;
  quantity: number;
  assignedTo: string;
}

interface ProductionFormState {
  notes: string;
  items: ProductionItem[];
  orderNumber?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

interface ProductHistoryEntry {
  id: string;
  _id: string;
  product: {
    _id: string;
    name: string;
    displayName: string;
  } | null;
  action: string;
  displayAction: string;
  quantity: number;
  reference: string;
  referenceType: string;
  referenceId: string;
  createdBy: any;
  createdAt: string;
  notes: string;
  isDamaged: boolean;
  previousStock: number;
  newStock: number;
}

interface EditForm {
  minStockLevel: number;
  maxStockLevel: number;
}

interface AvailableProduct {
  productId: string;
  productName: string;
  unit: string;
  departmentName: string;
  departmentId: string;
}

interface FactoryOrder {
  _id: string;
  id: string;
  orderNumber: string;
  items: FactoryOrderItem[];
  status: 'requested' | 'pending' | 'approved' | 'in_production' | 'completed' | 'stocked' | 'cancelled';
  notes: string;
  createdAt: string;
  createdBy: any;
  approvedBy?: any;
  approvedAt?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}

interface FactoryOrderItem {
  _id: string;
  id: string;
  productId: string;
  productName: string;
  product: {
    _id: string;
    name: string;
    nameEn?: string;
    department?: {
      _id: string;
      name: string;
      nameEn?: string;
    };
    unit?: string;
    unitEn?: string;
  };
  quantity: number;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'rejected';
  assignedTo?: {
    _id: string;
    userId: string;
    username: string;
    name: string;
    nameEn?: string;
    displayName: string;
    department?: any;
  };
  department?: {
    _id: string;
    name: string;
    nameEn?: string;
    displayName: string;
  };
  unit?: string;
  startedAt?: string;
  completedAt?: string;
  rejectReason?: string;
  notes?: string;
}

interface Chef {
  _id: string;
  id: string;
  user: string;
  userId: string;
  displayName: string;
  name?: string;
  nameEn?: string;
  department: Array<{
    _id: string;
    name: string;
    nameEn?: string;
    displayName: string;
  }>;
  status: 'active' | 'inactive';
}

interface Department {
  _id: string;
  name: string;
  nameEn: string;
  displayName: string;
}

interface ChefOrderItem extends FactoryOrderItem {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  orderCreatedAt: string;
}

// ========== المكونات المساعدة ==========
const formatInventoryFromBackend = (data: any, isRtl: boolean): FactoryInventoryItem => {
  return {
    id: data._id || data.id,
    _id: data._id || data.id,
    product: data.product ? {
      _id: data.product._id,
      name: data.product.name,
      nameEn: data.product.nameEn,
      displayName: data.product.displayName || (isRtl ? data.product.name : (data.product.nameEn || data.product.name)),
      code: data.product.code,
      unit: data.product.unit,
      unitEn: data.product.unitEn,
      displayUnit: data.product.displayUnit || (isRtl ? data.product.unit : (data.product.unitEn || data.product.unit)),
      department: data.product.department ? {
        _id: data.product.department._id,
        name: data.product.department.name,
        nameEn: data.product.department.nameEn,
        displayName: data.product.department.displayName || 
                   (isRtl ? data.product.department.name : (data.product.department.nameEn || data.product.department.name))
      } : null,
      price: data.product.price || 0,
      minStockLevel: data.product.minStockLevel || 10,
      maxStockLevel: data.product.maxStockLevel || 1000
    } : null,
    currentStock: data.currentStock || 0,
    pendingReturnStock: data.pendingReturnStock || 0,
    pendingShipStock: data.pendingShipStock || 0,
    damagedStock: data.damagedStock || 0,
    minStockLevel: data.minStockLevel || 10,
    maxStockLevel: data.maxStockLevel || 1000,
    availableStock: data.availableStock || (data.currentStock - (data.pendingReturnStock + data.pendingShipStock + data.damagedStock)),
    stockStatus: data.stockStatus || 'normal',
    displayStockStatus: data.displayStockStatus || 
                       (isRtl ? 
                         (data.stockStatus === 'low' ? 'منخفض' : 
                          data.stockStatus === 'high' ? 'مرتفع' : 'طبيعي') :
                         (data.stockStatus === 'low' ? 'Low' : 
                          data.stockStatus === 'high' ? 'High' : 'Normal')),
    createdBy: data.createdBy,
    updatedBy: data.updatedBy,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  };
};

// ========== مكونات المودالات ==========

// مكون تعيين الشيفات
interface AssignChefsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: FactoryOrder | null;
  assignFormData: { items: any[] };
  chefs: Chef[];
  error: string;
  submitting: string | null;
  assignChefs: (orderId: string) => void;
  setAssignForm: (formData: { items: any[] }) => void;
  isRtl: boolean;
  t: any;
}

export const AssignChefsModal: React.FC<AssignChefsModalProps> = ({
  isOpen,
  onClose,
  selectedOrder,
  assignFormData,
  chefs,
  error,
  submitting,
  assignChefs,
  setAssignForm,
  isRtl,
  t,
}) => {
  const availableChefsByDepartment = useMemo(() => {
    const map = new Map<string, Chef[]>();
    chefs.forEach((chef) => {
      if (Array.isArray(chef.department) && chef.department.length > 0) {
        chef.department.forEach((dept) => {
          const deptId = dept._id;
          if (deptId) {
            if (!map.has(deptId)) {
              map.set(deptId, []);
            }
            const chefsInDept = map.get(deptId)!;
            if (!chefsInDept.some(c => c._id === chef._id)) {
              chefsInDept.push(chef);
            }
          }
        });
      }
    });
    return map;
  }, [chefs]);

  const updateAssignment = useCallback(
    (index: number, field: string, value: string | boolean) => {
      setAssignForm({
        items: assignFormData.items.map((i, idx) =>
          idx === index ? { ...i, [field]: value } : i
        ),
      });
    },
    [assignFormData.items, setAssignForm]
  );

  useEffect(() => {
    if (!selectedOrder) return;

    const updatedItems = assignFormData.items.map((item) => {
      const orderItem = selectedOrder.items.find((i) => i._id === item.itemId);
      const departmentId = orderItem?.department?._id || orderItem?.product?.department?._id || '';
      const availableChefs = availableChefsByDepartment.get(departmentId) || [];

      let assignedTo = item.assignedTo;
      if (assignedTo === '' && availableChefs.length === 1 && !item.rejected) {
        assignedTo = availableChefs[0]._id;
      }

      return {
        ...item,
        assignedTo,
        unit: orderItem?.unit || orderItem?.product?.unit || 'unit',
        rejected: item.rejected || false,
        rejectReason: item.rejectReason || '',
      };
    });

    const hasChanges = updatedItems.some(
      (item, idx) =>
        item.assignedTo !== assignFormData.items[idx].assignedTo ||
        item.unit !== assignFormData.items[idx].unit ||
        item.rejected !== assignFormData.items[idx].rejected ||
        item.rejectReason !== assignFormData.items[idx].rejectReason
    );

    if (hasChanges) {
      setAssignForm({ items: updatedItems });
    }
  }, [assignFormData.items, availableChefsByDepartment, selectedOrder, setAssignForm, isRtl]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOrder) {
      assignChefs(selectedOrder._id);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isRtl ? `تعيين الشيفات لطلب ${selectedOrder?.orderNumber}` : `Assign Chefs to Order #${selectedOrder?.orderNumber}`}
      size="md"
      className="bg-white rounded-lg shadow-xl border border-gray-100"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {assignFormData.items.map((item, index) => {
          const orderItem = selectedOrder?.items.find((i) => i._id === item.itemId);
          const departmentId = orderItem?.department?._id || orderItem?.product?.department?._id || '';
          const availableChefs = availableChefsByDepartment.get(departmentId) || [];

          const chefOptions = availableChefs.map((chef) => {
            const deptName = Array.isArray(chef.department)
              ? chef.department.find(d => d._id === departmentId)?.displayName ||
                chef.department[0]?.displayName ||
                (isRtl ? 'غير معروف' : 'Unknown')
              : (isRtl ? 'غير معروف' : 'Unknown');
            
            return {
              value: chef._id,
              label: `${chef.displayName} (${deptName})`
            };
          });

          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
              className="space-y-2"
            >
              <div className="flex justify-between items-center flex-wrap gap-2">
                <label
                  className={`block text-sm font-medium text-gray-900 ${isRtl ? 'text-right' : 'text-left'}`}
                >
                  {isRtl
                    ? `تعيين شيف لـ ${orderItem?.productName || orderItem?.product?.name} (${item.quantity} ${item.unit})`
                    : `Assign chef to ${orderItem?.productName || orderItem?.product?.nameEn || orderItem?.product?.name} (${item.quantity} ${item.unit})`}
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.rejected}
                    onChange={(e) => updateAssignment(index, 'rejected', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  <span className="text-sm text-red-600">{isRtl ? 'رفض العنصر' : 'Reject Item'}</span>
                </label>
              </div>

              {item.rejected ? (
                <select
                  value={item.rejectReason || ''}
                  onChange={(e) => updateAssignment(index, 'rejectReason', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-red-300 rounded-md focus:ring-red-500 focus:border-red-500 bg-red-50"
                >
                  <option value="">{isRtl ? 'اختر سبب الرفض' : 'Select reject reason'}</option>
                  <option value="تالف">{isRtl ? 'تالف' : 'Damaged'}</option>
                  <option value="منتج خاطئ">{isRtl ? 'منتج خاطئ' : 'Wrong product'}</option>
                  <option value="كمية زائدة">{isRtl ? 'كمية زائدة' : 'Excess quantity'}</option>
                  <option value="أخرى">{isRtl ? 'أخرى' : 'Other'}</option>
                </select>
              ) : (
                <CustomDropdown
                  value={item.assignedTo}
                  onChange={(value) => updateAssignment(index, 'assignedTo', value as string)}
                  options={chefOptions}
                  placeholder={isRtl ? 'اختر شيف' : 'Select Chef'}
                  ariaLabel={isRtl ? 'اختر شيف' : 'Select Chef'}
                  className="w-full"
                />
              )}
            </motion.div>
          );
        })}

        {error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}
          >
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-600 text-sm">{error}</span>
          </motion.div>
        )}

        <div className={`flex justify-end gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <Button
            variant="secondary"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md px-4 py-2 text-sm shadow-sm"
            aria-label={isRtl ? 'إلغاء' : 'Cancel'}
          >
            {isRtl ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting !== null}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-4 py-2 text-sm shadow-sm flex items-center gap-2"
            aria-label={isRtl ? 'تعيين الشيفات' : 'Assign Chefs'}
          >
            {submitting ? (isRtl ? 'جارٍ التعيين...' : 'Assigning...') : (
              <>
                <ChefHat className="w-4 h-4" />
                {isRtl ? 'تعيين الشيفات' : 'Assign Chefs'}
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// مكون تفاصيل الطلب
interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: FactoryOrder | null;
  isRtl: boolean;
  t: any;
  user: any;
  onApproveOrder?: (orderId: string) => void;
  onUpdateItemStatus?: (orderId: string, itemId: string, status: string, notes?: string, rejectReason?: string) => void;
  onCompleteToInventory?: (orderId: string) => void;
  onCancelOrder?: (orderId: string, notes?: string) => void;
  onStartItem?: (orderId: string, itemId: string) => void;
}

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({
  isOpen,
  onClose,
  order,
  isRtl,
  t,
  user,
  onApproveOrder,
  onUpdateItemStatus,
  onCompleteToInventory,
  onCancelOrder,
  onStartItem,
}) => {
  const [selectedTab, setSelectedTab] = useState<'details' | 'items'>('details');
  const [cancelNotes, setCancelNotes] = useState('');
  const [itemNotes, setItemNotes] = useState<Record<string, string>>({});
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});
  const [showCancelForm, setShowCancelForm] = useState(false);
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'approved':
      case 'stocked':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_production':
      case 'in_progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending':
      case 'requested':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getItemStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'assigned':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRtl ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canApprove = order?.status === 'requested' && ['admin', 'production'].includes(user?.role);
  const canCancel = order?.status !== 'cancelled' && order?.status !== 'stocked' && 
    (['admin', 'production'].includes(user?.role) || order?.createdBy?._id === user?.id);
  const isChef = user?.role === 'chef';

  const isAllItemsCompleted = order?.items.every(item => item.status === 'completed' || item.status === 'rejected');
  const canCompleteToInventory = order?.status === 'in_production' && isAllItemsCompleted && ['admin', 'production'].includes(user?.role);

  const handleCancelOrder = () => {
    if (onCancelOrder && order) {
      onCancelOrder(order._id, cancelNotes);
      setCancelNotes('');
      setShowCancelForm(false);
    }
  };

  const handleStartItem = (itemId: string) => {
    if (onStartItem && order) {
      onStartItem(order._id, itemId);
    }
  };

  const handleCompleteItem = (itemId: string) => {
    if (onUpdateItemStatus && order) {
      onUpdateItemStatus(order._id, itemId, 'completed', itemNotes[itemId]);
    }
  };

  const handleRejectItem = (itemId: string) => {
    if (onUpdateItemStatus && order && rejectReasons[itemId]) {
      onUpdateItemStatus(order._id, itemId, 'rejected', undefined, rejectReasons[itemId]);
      setRejectReasons(prev => ({ ...prev, [itemId]: '' }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isRtl ? `تفاصيل الطلب ${order?.orderNumber}` : `Order Details #${order?.orderNumber}`}
      size="lg"
      className="bg-white rounded-lg shadow-xl border border-gray-100"
    >
      {order ? (
        <div className="space-y-6">
          {/* Order Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                  {order.status === 'requested' ? (isRtl ? 'مطلوب' : 'Requested') :
                   order.status === 'pending' ? (isRtl ? 'قيد الانتظار' : 'Pending') :
                   order.status === 'approved' ? (isRtl ? 'معتمد' : 'Approved') :
                   order.status === 'in_production' ? (isRtl ? 'في الإنتاج' : 'In Production') :
                   order.status === 'completed' ? (isRtl ? 'مكتمل' : 'Completed') :
                   order.status === 'stocked' ? (isRtl ? 'مخزن' : 'Stocked') :
                   (isRtl ? 'ملغى' : 'Cancelled')}
                </span>
                {order.priority && (
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    order.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                    order.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                    order.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {order.priority === 'urgent' ? (isRtl ? 'عاجل' : 'Urgent') :
                     order.priority === 'high' ? (isRtl ? 'عالية' : 'High') :
                     order.priority === 'medium' ? (isRtl ? 'متوسطة' : 'Medium') :
                     (isRtl ? 'منخفضة' : 'Low')}
                  </span>
                )}
                <span className="text-sm text-gray-600">{formatDate(order.createdAt)}</span>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {isRtl ? 'تم الإنشاء بواسطة' : 'Created by'}: {order.createdBy?.displayName || order.createdBy?.name || 'Unknown'}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {canApprove && onApproveOrder && (
                <Button
                  variant="primary"
                  onClick={() => onApproveOrder(order._id)}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {isRtl ? 'موافقة' : 'Approve'}
                </Button>
              )}
              
              {canCancel && !showCancelForm && (
                <Button
                  variant="secondary"
                  onClick={() => setShowCancelForm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm"
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {isRtl ? 'إلغاء الطلب' : 'Cancel Order'}
                </Button>
              )}
              
              {canCompleteToInventory && onCompleteToInventory && (
                <Button
                  variant="primary"
                  onClick={() => onCompleteToInventory(order._id)}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-sm"
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  {isRtl ? 'تأكيد الإنتاج' : 'Confirm Production'}
                </Button>
              )}
            </div>
          </div>

          {showCancelForm && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-sm font-medium text-red-700 mb-2">{isRtl ? 'سبب الإلغاء' : 'Cancellation Reason'}</h4>
              <textarea
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
                placeholder={isRtl ? 'أدخل سبب الإلغاء...' : 'Enter cancellation reason...'}
                className="w-full p-3 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                rows={3}
              />
              <div className="flex justify-end gap-2 mt-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowCancelForm(false)}
                  className="text-sm"
                >
                  {isRtl ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCancelOrder}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm"
                  disabled={!cancelNotes.trim()}
                >
                  {isRtl ? 'تأكيد الإلغاء' : 'Confirm Cancellation'}
                </Button>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className={`flex space-x-2 ${isRtl ? 'flex-row-reverse space-x-reverse' : ''}`}>
              <button
                onClick={() => setSelectedTab('details')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  selectedTab === 'details'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {isRtl ? 'تفاصيل الطلب' : 'Order Details'}
              </button>
              <button
                onClick={() => setSelectedTab('items')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  selectedTab === 'items'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {isRtl ? 'العناصر' : 'Items'} ({order.items.length})
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {selectedTab === 'details' ? (
            <div className="space-y-4">
              {order.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{isRtl ? 'ملاحظات' : 'Notes'}</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">{order.notes}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">{isRtl ? 'معلومات الإنشاء' : 'Creation Info'}</h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      {isRtl ? 'رقم الطلب' : 'Order Number'}: <span className="font-medium">{order.orderNumber}</span>
                    </p>
                    <p className="text-sm text-gray-600">
                      {isRtl ? 'تاريخ الإنشاء' : 'Created At'}: {formatDate(order.createdAt)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {isRtl ? 'بواسطة' : 'By'}: {order.createdBy?.displayName || order.createdBy?.name || 'Unknown'}
                    </p>
                  </div>
                </div>

                {order.approvedBy && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">{isRtl ? 'معلومات الموافقة' : 'Approval Info'}</h4>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        {isRtl ? 'تمت الموافقة بواسطة' : 'Approved By'}: {order.approvedBy?.displayName || order.approvedBy?.name || 'Unknown'}
                      </p>
                      {order.approvedAt && (
                        <p className="text-sm text-gray-600">
                          {isRtl ? 'تاريخ الموافقة' : 'Approved At'}: {formatDate(order.approvedAt)}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {order.items.map((item, index) => {
                const isChefAssigned = isChef && item.assignedTo?._id === user?._id;
                const canUpdateItem = ['admin', 'production', 'chef'].includes(user?.role) || isChefAssigned;

                return (
                  <motion.div
                    key={item._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.1 }}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {isRtl ? item.product?.name : item.product?.nameEn || item.product?.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {item.quantity} {item.unit || (isRtl ? 'وحدة' : 'unit')}
                        </p>
                        {item.rejectReason && (
                          <p className="text-sm text-red-600 mt-1">
                            {isRtl ? 'سبب الرفض' : 'Reject Reason'}: {item.rejectReason}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getItemStatusColor(item.status)}`}>
                          {item.status === 'pending' ? (isRtl ? 'قيد الانتظار' : 'Pending') :
                           item.status === 'assigned' ? (isRtl ? 'معين' : 'Assigned') :
                           item.status === 'in_progress' ? (isRtl ? 'قيد التنفيذ' : 'In Progress') :
                           item.status === 'rejected' ? (isRtl ? 'مرفوض' : 'Rejected') :
                           (isRtl ? 'مكتمل' : 'Completed')}
                        </span>
                      </div>
                    </div>

                    {item.assignedTo && (
                      <div className="mb-3">
                        <p className="text-sm text-gray-600">
                          {isRtl ? 'معين إلى' : 'Assigned to'}: {item.assignedTo.displayName || item.assignedTo.name}
                        </p>
                        {item.startedAt && (
                          <p className="text-sm text-gray-600">
                            {isRtl ? 'تاريخ البدء' : 'Started At'}: {formatDate(item.startedAt)}
                          </p>
                        )}
                        {item.completedAt && (
                          <p className="text-sm text-gray-600">
                            {isRtl ? 'تاريخ الإكمال' : 'Completed At'}: {formatDate(item.completedAt)}
                          </p>
                        )}
                      </div>
                    )}

                    {canUpdateItem && order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'stocked' && (
                      <div className="flex flex-col gap-3 mt-3">
                        {item.status === 'assigned' && isChefAssigned && (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleStartItem(item._id)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            {isRtl ? 'بدء الإنتاج' : 'Start Production'}
                          </Button>
                        )}
                        
                        {item.status === 'in_progress' && isChefAssigned && (
                          <>
                            <div className="space-y-2">
                              <textarea
                                value={itemNotes[item._id] || ''}
                                onChange={(e) => setItemNotes(prev => ({ ...prev, [item._id]: e.target.value }))}
                                placeholder={isRtl ? 'أدخل ملاحظات (اختياري)...' : 'Enter notes (optional)...'}
                                className="w-full p-2 border border-gray-300 rounded text-sm"
                                rows={2}
                              />
                              <div className="flex gap-2">
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => handleCompleteItem(item._id)}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  {isRtl ? 'إكمال العنصر' : 'Complete Item'}
                                </Button>
                                
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    value={rejectReasons[item._id] || ''}
                                    onChange={(e) => setRejectReasons(prev => ({ ...prev, [item._id]: e.target.value }))}
                                    placeholder={isRtl ? 'سبب الرفض...' : 'Reject reason...'}
                                    className="w-full p-2 border border-red-300 rounded text-sm"
                                  />
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => handleRejectItem(item._id)}
                                    className="mt-1 bg-red-600 hover:bg-red-700 text-white w-full"
                                    disabled={!rejectReasons[item._id]?.trim()}
                                  >
                                    {isRtl ? 'رفض العنصر' : 'Reject Item'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-600">
          {isRtl ? 'لا توجد بيانات للطلب' : 'No order data available'}
        </div>
      )}
    </Modal>
  );
};

// مكون طلبات الشيف
interface ChefOrdersViewProps {
  orders: FactoryOrder[];
  assignedItems: ChefOrderItem[];
  isRtl: boolean;
  t: any;
  user: any;
  onViewOrderDetails: (order: FactoryOrder) => void;
  onStartItem: (orderId: string, itemId: string) => void;
  onCompleteItem: (orderId: string, itemId: string, notes?: string) => void;
  onRejectItem: (orderId: string, itemId: string, rejectReason: string) => void;
}

const ChefOrdersView: React.FC<ChefOrdersViewProps> = ({
  orders,
  assignedItems,
  isRtl,
  t,
  user,
  onViewOrderDetails,
  onStartItem,
  onCompleteItem,
  onRejectItem,
}) => {
  const [selectedTab, setSelectedTab] = useState<'orders' | 'items'>('items');
  const [itemNotes, setItemNotes] = useState<Record<string, string>>({});
  const [rejectReasons, setRejectReasons] = useState<Record<string, string>>({});

  const myOrders = orders.filter(order => 
    order.items.some(item => item.assignedTo?._id === user?.id)
  );

  const myAssignedItems = assignedItems.filter(item => 
    item.assignedTo?._id === user?.id && item.status === 'assigned'
  );

  const myInProgressItems = assignedItems.filter(item => 
    item.assignedTo?._id === user?.id && item.status === 'in_progress'
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRtl ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleStartItem = (item: ChefOrderItem) => {
    onStartItem(item.orderId, item._id);
  };

  const handleCompleteItem = (item: ChefOrderItem) => {
    onCompleteItem(item.orderId, item._id, itemNotes[item._id]);
    setItemNotes(prev => ({ ...prev, [item._id]: '' }));
  };

  const handleRejectItem = (item: ChefOrderItem) => {
    if (rejectReasons[item._id]) {
      onRejectItem(item.orderId, item._id, rejectReasons[item._id]);
      setRejectReasons(prev => ({ ...prev, [item._id]: '' }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200">
        <nav className={`flex space-x-2 ${isRtl ? 'flex-row-reverse space-x-reverse' : ''}`}>
          <button
            onClick={() => setSelectedTab('items')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'items'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {isRtl ? 'المهام المعينة' : 'Assigned Items'} ({myAssignedItems.length + myInProgressItems.length})
          </button>
          <button
            onClick={() => setSelectedTab('orders')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              selectedTab === 'orders'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {isRtl ? 'طلباتي' : 'My Orders'} ({myOrders.length})
          </button>
        </nav>
      </div>

      {selectedTab === 'items' ? (
        <div className="space-y-4">
          {myAssignedItems.length === 0 && myInProgressItems.length === 0 ? (
            <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
              <ChefHat className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-sm font-medium">
                {isRtl ? 'لا توجد مهام معينة لك حالياً' : 'No assigned tasks available'}
              </p>
            </div>
          ) : (
            <>
              {myInProgressItems.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {isRtl ? 'المهام قيد التنفيذ' : 'In Progress Tasks'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myInProgressItems.map((item) => (
                      <div key={item._id} className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {isRtl ? item.product?.name : item.product?.nameEn || item.product?.name}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {item.quantity} {item.unit || (isRtl ? 'وحدة' : 'unit')}
                            </p>
                            <p className="text-sm text-gray-600">
                              {isRtl ? 'رقم الطلب' : 'Order'}: {item.orderNumber}
                            </p>
                            {item.startedAt && (
                              <p className="text-sm text-gray-600">
                                {isRtl ? 'بدأ في' : 'Started'}: {formatDate(item.startedAt)}
                              </p>
                            )}
                          </div>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            {isRtl ? 'قيد التنفيذ' : 'In Progress'}
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          <textarea
                            value={itemNotes[item._id] || ''}
                            onChange={(e) => setItemNotes(prev => ({ ...prev, [item._id]: e.target.value }))}
                            placeholder={isRtl ? 'ملاحظات الإكمال (اختياري)...' : 'Completion notes (optional)...'}
                            className="w-full p-2 border border-gray-300 rounded text-sm"
                            rows={2}
                          />
                          
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleCompleteItem(item)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {isRtl ? 'إكمال' : 'Complete'}
                            </Button>
                            
                            <div className="space-y-1">
                              <input
                                type="text"
                                value={rejectReasons[item._id] || ''}
                                onChange={(e) => setRejectReasons(prev => ({ ...prev, [item._id]: e.target.value }))}
                                placeholder={isRtl ? 'سبب الرفض' : 'Reject reason'}
                                className="w-full p-2 border border-red-300 rounded text-sm"
                              />
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => handleRejectItem(item)}
                                className="bg-red-600 hover:bg-red-700 text-white w-full"
                                disabled={!rejectReasons[item._id]?.trim()}
                              >
                                {isRtl ? 'رفض' : 'Reject'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {myAssignedItems.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {isRtl ? 'مهام جديدة' : 'New Tasks'} ({myAssignedItems.length})
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {myAssignedItems.map((item) => (
                      <div key={item._id} className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {isRtl ? item.product?.name : item.product?.nameEn || item.product?.name}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {item.quantity} {item.unit || (isRtl ? 'وحدة' : 'unit')}
                            </p>
                            <p className="text-sm text-gray-600">
                              {isRtl ? 'رقم الطلب' : 'Order'}: {item.orderNumber}
                            </p>
                            <p className="text-sm text-gray-600">
                              {isRtl ? 'تاريخ الطلب' : 'Order Date'}: {formatDate(item.orderCreatedAt)}
                            </p>
                          </div>
                          <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            {isRtl ? 'معين' : 'Assigned'}
                          </span>
                        </div>
                        
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleStartItem(item)}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          {isRtl ? 'بدء الإنتاج' : 'Start Production'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {myOrders.length === 0 ? (
            <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-sm font-medium">
                {isRtl ? 'لا توجد طلبات حالياً' : 'No orders available'}
              </p>
            </div>
          ) : (
            myOrders.map((order) => (
              <div key={order._id} className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-gray-900">{order.orderNumber}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        order.status === 'in_production' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.status === 'approved' ? (isRtl ? 'معتمد' : 'Approved') :
                         order.status === 'in_production' ? (isRtl ? 'في الإنتاج' : 'In Production') :
                         (isRtl ? 'مطلوب' : 'Requested')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {isRtl ? 'تاريخ الإنشاء' : 'Created At'}: {formatDate(order.createdAt)}
                    </p>
                  </div>
                  
                  <button
                    onClick={() => onViewOrderDetails(order)}
                    className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
                  >
                    {t.viewDetails}
                  </button>
                </div>
                
                <div className="space-y-2">
                  {order.items
                    .filter(item => item.assignedTo?._id === user?.id)
                    .map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                        <div>
                          <span className="text-gray-900">
                            {isRtl ? item.product?.name : item.product?.nameEn || item.product?.name}
                          </span>
                          <span className="text-gray-600 ml-2">
                            ({item.quantity} {item.unit || (isRtl ? 'وحدة' : 'unit')})
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.status === 'completed' ? 'bg-green-100 text-green-800' :
                          item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                          item.status === 'assigned' ? 'bg-purple-100 text-purple-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status === 'pending' ? (isRtl ? 'قيد الانتظار' : 'Pending') :
                           item.status === 'assigned' ? (isRtl ? 'معين' : 'Assigned') :
                           item.status === 'in_progress' ? (isRtl ? 'قيد التنفيذ' : 'In Progress') :
                           item.status === 'completed' ? (isRtl ? 'مكتمل' : 'Completed') :
                           (isRtl ? 'مرفوض' : 'Rejected')}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ========== الترجمات ==========
const translations = {
  ar: {
    title: 'إدارة مخزون المصنع',
    description: 'إدارة مخزون المصنع وطلبات الإنتاج',
    noItems: 'لا توجد عناصر في مخزون المصنع',
    noHistory: 'لا يوجد سجل لهذا المنتج',
    stock: 'المخزون الحالي',
    minStock: 'الحد الأدنى للمخزون',
    maxStock: 'الحد الأقصى للمخزون',
    unit: 'الوحدة',
    lowStock: 'مخزون منخفض',
    normal: 'عادي',
    high: 'مرتفع',
    create: 'إنشاء طلب إنتاج',
    viewDetails: 'عرض التفاصيل',
    editStockLimits: 'تعديل حدود المخزون',
    search: 'البحث عن المنتجات...',
    selectProduct: 'اختر منتج',
    selectChef: 'اختر شيف',
    filterByStatus: 'تصفية حسب الحالة',
    filterByDepartment: 'تصفية حسب القسم',
    allStatuses: 'جميع الحالات',
    allDepartments: 'جميع الأقسام',
    notes: 'ملاحظات',
    notesPlaceholder: 'أدخل ملاحظات إضافية (اختياري)',
    items: 'العناصر',
    chef: 'الشيف',
    addItem: 'إضافة عنصر',
    removeItem: 'إزالة العنصر',
    submit: 'إرسال',
    submitting: 'جاري الإرسال...',
    cancel: 'إلغاء',
    save: 'حفظ',
    saving: 'جاري الحفظ...',
    productDetails: 'تفاصيل المنتج',
    date: 'التاريخ',
    type: 'النوع',
    quantity: 'الكمية',
    reference: 'المرجع',
    produced_stock: 'إنتاج مخزون',
    adjustment: 'تعديل',
    inProduction: 'في الإنتاج',
    assignChefs: 'تعيين الشيفات',
    manageAssignments: 'إدارة التعيينات',
    viewOrders: 'عرض الطلبات',
    pendingOrders: 'طلبات قيد الانتظار',
    myOrders: 'طلباتي',
    myTasks: 'مهامي',
    allOrders: 'جميع الطلبات',
    orderNumber: 'رقم الطلب',
    orderStatus: 'حالة الطلب',
    orderDate: 'تاريخ الطلب',
    createdBy: 'تم الإنشاء بواسطة',
    assignedTo: 'معين إلى',
    startProduction: 'بدء الإنتاج',
    completeItem: 'إكمال العنصر',
    completeOrder: 'إكمال الطلب',
    approveOrder: 'الموافقة على الطلب',
    cancelOrder: 'إلغاء الطلب',
    moveToInventory: 'نقل للمخزون',
    confirmProduction: 'تأكيد الإنتاج',
    assignedItems: 'العناصر المعينة',
    stats: {
      totalInventory: 'إجمالي المخزون',
      lowStock: 'مخزون منخفض',
      inProduction: 'في الإنتاج',
      pendingOrders: 'طلبات قيد الانتظار',
      myTasks: 'مهامي',
    },
    errors: {
      fetchInventory: 'خطأ في جلب بيانات مخزون المصنع',
      createProduction: 'خطأ في إنشاء طلب الإنتاج',
      updateInventory: 'خطأ في تحديث المخزون',
      invalidForm: 'البيانات المدخلة غير صالحة',
      required: 'حقل {field} مطلوب',
      nonNegative: 'يجب أن يكون {field} غير سالب',
      maxGreaterMin: 'الحد الأقصى للمخزون يجب أن يكون أكبر من الحد الأدنى',
      invalidQuantityMax: 'الكمية يجب أن تكون أكبر من 0',
      noItemSelected: 'لم يتم اختيار عنصر',
      invalidProductId: 'معرف المنتج غير صالح',
      invalidChefId: 'معرف الشيف غير صالح',
      productNotFound: 'المنتج غير موجود',
      tooManyRequests: 'طلبات كثيرة جدًا، حاول مرة أخرى لاحقًا',
      duplicateProduct: 'لا يمكن إضافة نفس المنتج أكثر من مرة',
      chefNoDepartments: 'ليس لديك أقسام معينة',
      productNotInDepartment: 'هذا المنتج ليس في أقسامك',
      unauthorizedAccess: 'غير مصرح بالوصول',
    },
    notifications: {
      productionCreated: 'تم إنشاء طلب الإنتاج بنجاح',
      inventoryUpdated: 'تم تحديث المخزون بنجاح',
      taskAssigned: 'تم تعيين مهمة إنتاج جديدة',
      orderCompleted: 'تم إكمال طلب الإنتاج',
      chefsAssigned: 'تم تعيين الشيفات بنجاح',
      orderApproved: 'تمت الموافقة على الطلب',
      itemStatusUpdated: 'تم تحديث حالة العنصر',
      orderMovedToInventory: 'تم نقل الطلب إلى المخزون بنجاح',
      itemStarted: 'تم بدء إنتاج العنصر',
      itemCompleted: 'تم إكمال العنصر',
      itemRejected: 'تم رفض العنصر',
      orderCancelled: 'تم إلغاء الطلب',
    },
  },
  en: {
    title: 'Factory Inventory Management',
    description: 'Manage factory inventory and production orders',
    noItems: 'No items found in factory inventory',
    noHistory: 'No history available for this product',
    stock: 'Current Stock',
    minStock: 'Minimum Stock',
    maxStock: 'Maximum Stock',
    unit: 'Unit',
    lowStock: 'Low Stock',
    normal: 'Normal',
    high: 'High',
    create: 'Create Production Order',
    viewDetails: 'View Details',
    editStockLimits: 'Edit Stock Limits',
    search: 'Search products...',
    selectProduct: 'Select Product',
    selectChef: 'Select Chef',
    filterByStatus: 'Filter by Status',
    filterByDepartment: 'Filter by Department',
    allStatuses: 'All Statuses',
    allDepartments: 'All Departments',
    notes: 'Notes',
    notesPlaceholder: 'Enter additional notes (optional)',
    items: 'Items',
    chef: 'Chef',
    addItem: 'Add Item',
    removeItem: 'Remove Item',
    submit: 'Submit',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    productDetails: 'Product Details',
    date: 'Date',
    type: 'Type',
    quantity: 'Quantity',
    reference: 'Reference',
    produced_stock: 'Produced Stock',
    adjustment: 'Adjustment',
    inProduction: 'In Production',
    assignChefs: 'Assign Chefs',
    manageAssignments: 'Manage Assignments',
    viewOrders: 'View Orders',
    pendingOrders: 'Pending Orders',
    myOrders: 'My Orders',
    myTasks: 'My Tasks',
    allOrders: 'All Orders',
    orderNumber: 'Order Number',
    orderStatus: 'Order Status',
    orderDate: 'Order Date',
    createdBy: 'Created By',
    assignedTo: 'Assigned To',
    startProduction: 'Start Production',
    completeItem: 'Complete Item',
    completeOrder: 'Complete Order',
    approveOrder: 'Approve Order',
    cancelOrder: 'Cancel Order',
    moveToInventory: 'Move to Inventory',
    confirmProduction: 'Confirm Production',
    assignedItems: 'Assigned Items',
    stats: {
      totalInventory: 'Total Inventory',
      lowStock: 'Low Stock',
      inProduction: 'In Production',
      pendingOrders: 'Pending Orders',
      myTasks: 'My Tasks',
    },
    errors: {
      fetchInventory: 'Error fetching factory inventory data',
      createProduction: 'Error creating production order',
      updateInventory: 'Error updating inventory',
      invalidForm: 'Invalid form data',
      required: '{field} is required',
      nonNegative: '{field} must be non-negative',
      maxGreaterMin: 'Maximum stock must be greater than minimum stock',
      invalidQuantityMax: 'Quantity must be greater than 0',
      noItemSelected: 'No item selected',
      invalidProductId: 'Invalid product ID',
      invalidChefId: 'Invalid chef ID',
      productNotFound: 'Product not found',
      tooManyRequests: 'Too many requests, please try again later',
      duplicateProduct: 'Cannot add the same product multiple times',
      chefNoDepartments: 'No departments assigned',
      productNotInDepartment: 'This product is not in your departments',
      unauthorizedAccess: 'Unauthorized access',
    },
    notifications: {
      productionCreated: 'Production order created successfully',
      inventoryUpdated: 'Inventory updated successfully',
      taskAssigned: 'New production task assigned',
      orderCompleted: 'Production order completed',
      chefsAssigned: 'Chefs assigned successfully',
      orderApproved: 'Order approved successfully',
      itemStatusUpdated: 'Item status updated successfully',
      orderMovedToInventory: 'Order moved to inventory successfully',
      itemStarted: 'Item production started successfully',
      itemCompleted: 'Item completed successfully',
      itemRejected: 'Item rejected successfully',
      orderCancelled: 'Order cancelled successfully',
    },
  },
};

// ========== مكون إدخال الكمية ==========
const QuantityInput = ({
  value,
  onChange,
  onIncrement,
  onDecrement,
}: {
  value: number;
  onChange: (val: string) => void;
  onIncrement: () => void;
  onDecrement: () => void;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const t = translations[isRtl ? 'ar' : 'en'];
  const handleChange = (val: string) => {
    const num = parseInt(val, 10);
    if (val === '' || isNaN(num) || num < 1) {
      onChange('1');
      return;
    }
    onChange(val);
  };
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onDecrement}
        className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors duration-200 flex items-center justify-center disabled:opacity-50"
        aria-label={isRtl ? 'تقليل الكمية' : 'Decrease quantity'}
        disabled={value <= 1}
      >
        <Minus className="w-4 h-4" />
      </button>
      <input
        type="number"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        min={1}
        className="w-12 h-8 text-center border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
        style={{ appearance: 'none', MozAppearance: 'textfield' }}
        aria-label={isRtl ? 'الكمية' : 'Quantity'}
      />
      <button
        onClick={onIncrement}
        className="w-8 h-8 bg-amber-600 hover:bg-amber-700 rounded-full transition-colors duration-200 flex items-center justify-center"
        aria-label={isRtl ? 'زيادة الكمية' : 'Increase quantity'}
      >
        <Plus className="w-4 h-4 text-white" />
      </button>
    </div>
  );
};

// ========== مكون اختيار المنتج ==========
export const ProductDropdown = ({
  value,
  onChange,
  options,
  ariaLabel,
  disabled = false,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  ariaLabel: string;
  disabled?: boolean;
  placeholder?: string;
}) => {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption =
    options.find((opt) => opt.value === value) ||
    { value: '', label: placeholder || (isRtl ? 'اختر' : 'Select') };
  return (
    <div className="relative group">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-gradient-to-r from-white to-gray-50 shadow-sm hover:shadow-md text-sm text-gray-700 ${isRtl ? 'text-right' : 'text-left'} flex justify-between items-center ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        aria-label={ariaLabel}
      >
        <span className="truncate">{selectedOption.label || (isRtl ? 'غير معروف' : 'Unknown')}</span>
        <div className={`${isOpen ? 'rotate-180' : 'rotate-0'} transition-transform duration-200`}>
          <ChevronDown className="w-5 h-5 text-gray-400 group-focus-within:text-amber-500 transition-colors" />
        </div>
      </button>
      {isOpen && !disabled && (
        <div className="absolute w-full mt-2 bg-white rounded-lg shadow-2xl border border-gray-100 z-20 max-h-60 overflow-y-auto scrollbar-none">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 hover:text-amber-600 cursor-pointer transition-colors duration-200"
            >
              {option.label || (isRtl ? 'غير معروف' : 'Unknown')}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== Reducer للنموذج ==========
type ProductionFormAction =
  | { type: 'SET_NOTES'; payload: string }
  | { type: 'SET_ORDER_NUMBER'; payload: string }
  | { type: 'SET_PRIORITY'; payload: 'low' | 'medium' | 'high' | 'urgent' }
  | { type: 'ADD_ITEM'; payload: ProductionItem }
  | { type: 'UPDATE_ITEM'; payload: { index: number; field: keyof ProductionItem; value: string | number } }
  | { type: 'REMOVE_ITEM'; payload: number }
  | { type: 'RESET' }
  | { type: 'INITIALIZE_ITEMS'; payload: ProductionItem[] };

const productionFormReducer = (state: ProductionFormState, action: ProductionFormAction): ProductionFormState => {
  switch (action.type) {
    case 'SET_NOTES':
      return { ...state, notes: action.payload };
    case 'SET_ORDER_NUMBER':
      return { ...state, orderNumber: action.payload };
    case 'SET_PRIORITY':
      return { ...state, priority: action.payload };
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.payload] };
    case 'UPDATE_ITEM':
      const newItems = [...state.items];
      newItems[action.payload.index] = { ...newItems[action.payload.index], [action.payload.field]: action.payload.value };
      return { ...state, items: newItems };
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((_, i) => i !== action.payload) };
    case 'INITIALIZE_ITEMS':
      return { ...state, items: action.payload };
    case 'RESET':
      return { notes: '', items: [], priority: 'medium' };
    default:
      return state;
  }
};

// ========== مكون نموذج إنشاء طلب إنتاج ==========
export const ProductionOrderForm: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  preselectedItem?: FactoryInventoryItem | null;
  availableProducts: AvailableProduct[];
  chefsData: Chef[];
  isChef: boolean;
  user: any;
  productDepartmentMap: Record<string, string>;
  createProductionMutation: any;
  t: any;
  isRtl: boolean;
  socket: any;
  isConnected: boolean;
}> = ({
  isOpen,
  onClose,
  preselectedItem,
  availableProducts,
  chefsData,
  isChef,
  user,
  productDepartmentMap,
  createProductionMutation,
  t,
  isRtl,
  socket,
  isConnected,
}) => {
  const [productionForm, dispatchProductionForm] = useReducer(productionFormReducer, { 
    notes: '', 
    items: [], 
    priority: 'medium' 
  });
  const [productionErrors, setProductionErrors] = useState<Record<string, string>>({});

  const chefDepartments = useMemo(() => {
    if (!isChef || !user?.id) return [];
    const storedDepts = localStorage.getItem('chefDepartments');
    if (storedDepts) {
      try {
        const depts = JSON.parse(storedDepts);
        return Array.isArray(depts) ? depts.map(dept => dept._id || dept) : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  }, [isChef, user]);

  const productOptions = useMemo(() => {
    let filtered = availableProducts;
    
    if (isChef && chefDepartments.length > 0) {
      filtered = filtered.filter((p) => 
        p.departmentId && chefDepartments.includes(p.departmentId)
      );
      
      if (preselectedItem) {
        const preselectedDeptId = preselectedItem.product?.department?._id;
        if (preselectedDeptId && !chefDepartments.includes(preselectedDeptId)) {
          toast.error(t.errors.productNotInDepartment, { 
            position: isRtl ? 'top-right' : 'top-left' 
          });
          setTimeout(() => onClose(), 1000);
          return [{ value: '', label: t.selectProduct }];
        }
      }
    }
    
    return [
      { value: '', label: t.selectProduct },
      ...filtered.map((product) => ({
        value: product.productId,
        label: `${product.productName} (${t.unit}: ${product.unit}) - ${product.departmentName}`,
      })),
    ];
  }, [availableProducts, isChef, chefDepartments, t, preselectedItem, isRtl, onClose]);

  useEffect(() => {
    if (preselectedItem?.product?._id) {
      const deptId = preselectedItem.product.department?._id;
      if (isChef && deptId && !chefDepartments.includes(deptId)) {
        toast.error(t.errors.productNotInDepartment, { position: isRtl ? 'top-right' : 'top-left' });
        onClose();
        return;
      }
      dispatchProductionForm({
        type: 'INITIALIZE_ITEMS',
        payload: [
          {
            product: preselectedItem.product._id,
            quantity: 1,
            assignedTo: isChef ? user?._id || '' : '',
          },
        ],
      });
    } else {
      dispatchProductionForm({
        type: 'INITIALIZE_ITEMS',
        payload: [{ product: '', quantity: 1, assignedTo: isChef ? user?._id || '' : '' }],
      });
    }
    dispatchProductionForm({
      type: 'SET_ORDER_NUMBER',
      payload: `PROD-${Date.now().toString().slice(-6)}`
    });
  }, [preselectedItem, isChef, user, chefDepartments, t, isRtl, onClose]);

  const getChefOptions = useCallback(
    (departmentId: string) => {
      const options = [
        { value: '', label: t.selectChef },
        ...chefsData
          ?.filter((chef) => chef.department.some(d => d._id === departmentId))
          .map((chef) => ({
            value: chef._id,
            label: isRtl
              ? chef.displayName || chef.name || `شيف بدون اسم (${chef._id})`
              : chef.displayName || chef.nameEn || `Unnamed Chef (${chef._id})`,
          })) || [],
      ];
      return options;
    },
    [chefsData, isRtl, t]
  );

  const addItemToForm = useCallback(() => {
    dispatchProductionForm({
      type: 'ADD_ITEM',
      payload: { product: '', quantity: 1, assignedTo: isChef ? user?._id || '' : '' },
    });
  }, [isChef, user]);

  const updateItemInForm = useCallback((index: number, field: keyof ProductionItem, value: string | number) => {
    if (field === 'quantity' && typeof value === 'string') {
      const numValue = parseInt(value, 10);
      if (isNaN(numValue) || numValue < 1) {
        setProductionErrors((prev) => ({
          ...prev,
          [`item_${index}_quantity`]: t.errors.invalidQuantityMax,
        }));
        return;
      }
      value = numValue;
    }
    dispatchProductionForm({ type: 'UPDATE_ITEM', payload: { index, field, value } });
  }, [t]);

  const handleProductChange = useCallback(
    (index: number, productId: string) => {
      if (!isValidObjectId(productId)) {
        setProductionErrors((prev) => ({
          ...prev,
          [`item_${index}_product`]: t.errors.invalidProductId,
        }));
        return;
      }
      if (productionForm.items.some((item, i) => i !== index && item.product === productId)) {
        setProductionErrors((prev) => ({
          ...prev,
          [`item_${index}_product`]: t.errors.duplicateProduct,
        }));
        return;
      }
      const deptId = productDepartmentMap[productId];
      if (isChef && deptId && !chefDepartments.includes(deptId)) {
        setProductionErrors((prev) => ({
          ...prev,
          [`item_${index}_product`]: t.errors.productNotInDepartment,
        }));
        return;
      }
      dispatchProductionForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'product', value: productId },
      });
      
      if (!isChef) {
        dispatchProductionForm({
          type: 'UPDATE_ITEM',
          payload: { index, field: 'assignedTo', value: '' },
        });
      }
    },
    [t, productionForm.items, isChef, chefDepartments, productDepartmentMap]
  );

  const handleChefChange = useCallback(
    (index: number, chefId: string) => {
      if (!isValidObjectId(chefId)) {
        setProductionErrors((prev) => ({
          ...prev,
          [`item_${index}_assignedTo`]: t.errors.invalidChefId,
        }));
        return;
      }
      dispatchProductionForm({
        type: 'UPDATE_ITEM',
        payload: { index, field: 'assignedTo', value: chefId },
      });
    },
    [t]
  );

  const removeItemFromForm = useCallback((index: number) => {
    dispatchProductionForm({ type: 'REMOVE_ITEM', payload: index });
    setProductionErrors((prev) => {
      const newErrors = { ...prev };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(`item_${index}_`)) delete newErrors[key];
      });
      return newErrors;
    });
  }, []);

  const validateProductionForm = useCallback(() => {
    const errors: Record<string, string> = {};
    if (productionForm.items.length === 0) {
      errors.items = t.errors.required.replace('{field}', t.items);
    }
    productionForm.items.forEach((item, index) => {
      if (!item.product) {
        errors[`item_${index}_product`] = t.errors.required.replace('{field}', t.selectProduct);
      } else if (!isValidObjectId(item.product)) {
        errors[`item_${index}_product`] = t.errors.invalidProductId;
      }
      const deptId = productDepartmentMap[item.product];
      if (isChef && deptId && !chefDepartments.includes(deptId)) {
        errors[`item_${index}_product`] = t.errors.productNotInDepartment;
      }
      if (!isChef) {
        if (!item.assignedTo) {
          errors[`item_${index}_assignedTo`] = t.errors.required.replace('{field}', t.chef);
        } else if (!isValidObjectId(item.assignedTo)) {
          errors[`item_${index}_assignedTo`] = t.errors.invalidChefId;
        }
      }
      if (item.quantity < 1 || isNaN(item.quantity)) {
        errors[`item_${index}_quantity`] = t.errors.invalidQuantityMax;
      }
    });
    setProductionErrors(errors);
    return Object.keys(errors).length === 0;
  }, [productionForm, t, isChef, chefDepartments, productDepartmentMap]);

  const handleSubmit = () => {
    if (!validateProductionForm()) return;
    createProductionMutation.mutate(
      {
        productionForm,
        user,
        socket,
        isConnected,
        isRtl,
        t,
      },
      {
        onSuccess: () => {
          onClose();
          dispatchProductionForm({ type: 'RESET' });
          setProductionErrors({});
        },
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: isOpen ? 1 : 0 }}
      className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ${
        isOpen ? '' : 'pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={t.create}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: isOpen ? 1 : 0.95, y: isOpen ? 0 : 20 }}
        className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t.create}</h2>
          <button
            onClick={onClose}
            className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors duration-200"
            aria-label={t.cancel}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{isRtl ? 'رقم الطلب' : 'Order Number'}</label>
              <input
                type="text"
                value={productionForm.orderNumber || ''}
                onChange={(e) => dispatchProductionForm({ type: 'SET_ORDER_NUMBER', payload: e.target.value })}
                placeholder={isRtl ? 'رقم الطلب...' : 'Order number...'}
                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{isRtl ? 'الأولوية' : 'Priority'}</label>
              <select
                value={productionForm.priority || 'medium'}
                onChange={(e) => dispatchProductionForm({ 
                  type: 'SET_PRIORITY', 
                  payload: e.target.value as 'low' | 'medium' | 'high' | 'urgent' 
                })}
                className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm"
              >
                <option value="low">{isRtl ? 'منخفضة' : 'Low'}</option>
                <option value="medium">{isRtl ? 'متوسطة' : 'Medium'}</option>
                <option value="high">{isRtl ? 'عالية' : 'High'}</option>
                <option value="urgent">{isRtl ? 'عاجلة' : 'Urgent'}</option>
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.notes}</label>
            <textarea
              value={productionForm.notes}
              onChange={(e) => dispatchProductionForm({ type: 'SET_NOTES', payload: e.target.value })}
              placeholder={t.notesPlaceholder}
              className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm resize-none"
              rows={3}
              aria-label={t.notes}
            />
            {productionErrors.form && <p className="text-red-600 text-xs mt-1">{productionErrors.form}</p>}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t.items}</label>
            {productionForm.items.map((item, index) => {
              const itemDeptId = productDepartmentMap[item.product] || '';
              const itemChefOptions = getChefOptions(itemDeptId);
              const isProductDisabled = !!preselectedItem && index === 0;
              return (
                <div key={index} className="flex flex-col gap-4 mb-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <ProductDropdown
                    value={item.product}
                    onChange={(value) => handleProductChange(index, value)}
                    options={productOptions}
                    ariaLabel={`${t.items} ${index + 1}`}
                    placeholder={t.selectProduct}
                    disabled={isProductDisabled}
                  />
                  {productionErrors[`item_${index}_product`] && (
                    <p className="text-red-600 text-xs">{productionErrors[`item_${index}_product`]}</p>
                  )}
                  {!isChef && (
                    <>
                      <ProductDropdown
                        value={item.assignedTo}
                        onChange={(value) => handleChefChange(index, value)}
                        options={itemChefOptions}
                        ariaLabel={`${t.items} ${index + 1} ${t.chef}`}
                        placeholder={t.selectChef}
                      />
                      {productionErrors[`item_${index}_assignedTo`] && (
                        <p className="text-red-600 text-xs">{productionErrors[`item_${index}_assignedTo`]}</p>
                      )}
                    </>
                  )}
                  {isChef && (
                    <p className="text-sm text-gray-600">
                      {t.chef}: {isRtl ? user?.name : user?.nameEn || user?.name}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">{t.quantity}</label>
                      <QuantityInput
                        value={item.quantity}
                        onChange={(val) => updateItemInForm(index, 'quantity', val)}
                        onIncrement={() => updateItemInForm(index, 'quantity', item.quantity + 1)}
                        onDecrement={() => updateItemInForm(index, 'quantity', Math.max(item.quantity - 1, 1))}
                      />
                      {productionErrors[`item_${index}_quantity`] && (
                        <p className="text-red-600 text-xs mt-1">{productionErrors[`item_${index}_quantity`]}</p>
                      )}
                    </div>
                    {productionForm.items.length > 1 && (
                      <button
                        onClick={() => removeItemFromForm(index)}
                        className="p-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-lg transition-colors duration-200 self-start sm:self-end"
                        aria-label={t.removeItem}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
            <button
              onClick={addItemToForm}
              className="flex items-center gap-2 text-amber-600 hover:text-amber-800 text-sm font-medium"
              aria-label={t.addItem}
            >
              <Plus className="w-4 h-4" />
              {t.addItem}
            </button>
            {productionErrors.items && <p className="text-red-600 text-xs">{productionErrors.items}</p>}
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors duration-200"
              aria-label={t.cancel}
            >
              {t.cancel}
            </button>
            <button
              onClick={handleSubmit}
              disabled={createProductionMutation.isLoading}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
              aria-label={createProductionMutation.isLoading ? t.submitting : t.submit}
            >
              {createProductionMutation.isLoading ? t.submitting : t.submit}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ========== المكون الرئيسي ==========
export const FactoryInventory: React.FC = () => {
  const { t: languageT, language } = useLanguage();
  const isRtl = language === 'ar';
  const t = translations[isRtl ? 'ar' : 'en'];
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<InventoryStatus | ''>('');
  const [filterDepartment, setFilterDepartment] = useState<string>('');
  const [isProductionModalOpen, setIsProductionModalOpen] = useState(false);
  const [preselectedItem, setPreselectedItem] = useState<FactoryInventoryItem | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FactoryInventoryItem | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editForm, setEditForm] = useState<EditForm>({ minStockLevel: 0, maxStockLevel: 0 });
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [availableProducts, setAvailableProducts] = useState<AvailableProduct[]>([]);
  const [userDepartments, setUserDepartments] = useState<string[]>([]);
  const [allDepartments, setAllDepartments] = useState<Department[]>([]);
  
  // Orders state
  const [viewMode, setViewMode] = useState<'inventory' | 'orders' | 'chef'>('inventory');
  const [orderFilterStatus, setOrderFilterStatus] = useState<string>('');
  const [selectedOrder, setSelectedOrder] = useState<FactoryOrder | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  
  // Assign chefs modal state
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignFormData, setAssignFormData] = useState<{ items: any[] }>({ items: [] });
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  
  const ITEMS_PER_PAGE = 10;
  const isChef = user?.role === 'chef';

  // Custom debounce hook
  const useDebouncedState = <T,>(initialValue: T, delay: number) => {
    const [value, setValue] = useState<T>(initialValue);
    const [debouncedValue, setDebouncedValue] = useState<T>(initialValue);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return [value, setValue, debouncedValue] as const;
  };
  const [searchInput, setSearchInput, debouncedSearchQuery] = useDebouncedState<string>('', 300);

  // جلب أقسام المستخدم (خاصة بالشيفات)
  useEffect(() => {
    const fetchUserDepartments = async () => {
      if (isChef && user?.id) {
        try {
          const response = await chefsAPI.getByUserId(user.id);
          const chefData = response.data || response;
          
          if (chefData?.department) {
            const departments = Array.isArray(chefData.department) 
              ? chefData.department 
              : [chefData.department];
            
            const deptIds = departments.map(dept => dept._id || dept);
            setUserDepartments(deptIds);
            
            localStorage.setItem('chefDepartments', JSON.stringify(departments));
            
            if (deptIds.length === 0) {
              toast.info(isRtl 
                ? 'ليس لديك أقسام معينة. يرجى الاتصال بالإدارة.' 
                : 'No departments assigned. Please contact administration.',
                { position: isRtl ? 'top-right' : 'top-left' }
              );
            }
          }
        } catch (err: any) {
          console.error('Error fetching chef departments:', err);
          toast.error(err.message || t.errors.chefNoDepartments, { 
            position: isRtl ? 'top-right' : 'top-left' 
          });
        }
      } else if (user?.department?._id) {
        setUserDepartments([user.department._id]);
      }
    };
    
    fetchUserDepartments();
  }, [isChef, user, t, isRtl]);

  // جلب جميع الأقسام
  useEffect(() => {
    const fetchAllDepartments = async () => {
      try {
        const response = await departmentAPI.getAll();
        const departments = Array.isArray(response.data) ? response.data : 
                           (Array.isArray(response) ? response : []);
        setAllDepartments(departments.map((dept: any) => ({
          _id: dept._id,
          name: dept.name,
          nameEn: dept.nameEn,
          displayName: isRtl ? dept.name : (dept.nameEn || dept.name)
        })));
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchAllDepartments();
  }, [isRtl]);

  // Factory Orders Query
  const { data: factoryOrdersData, refetch: refetchFactoryOrders } = useQuery<FactoryOrder[], Error>({
    queryKey: ['factoryOrders', language, orderFilterStatus, user?.id],
    queryFn: async () => {
      const query: any = { lang: language };
      if (orderFilterStatus) query.status = orderFilterStatus;
      
      const response = await factoryOrdersAPI.getAll(query);
      const data = response.data?.data || response?.data || [];
      if (!Array.isArray(data)) {
        console.warn('Invalid factory orders data:', data);
        return [];
      }
      
      // فلترة الطلبات حسب أقسام الشيف
      let filteredData = data;
      if (isChef && userDepartments.length > 0) {
        filteredData = data.filter((order: any) => {
          return order.items?.some((item: any) => {
            const productDeptId = item.product?.department?._id || 
                                 item.product?.department ||
                                 item.department?._id;
            return productDeptId && userDepartments.includes(productDeptId);
          }) || 
          order.items?.some((item: any) => 
            item.assignedTo?._id === user?.id
          );
        });
      }
      
      return filteredData.map((order: any) => ({
        _id: order._id || order.id,
        id: order._id || order.id,
        orderNumber: order.orderNumber,
        items: (order.items || []).map((item: any) => ({
          _id: item._id,
          id: item._id,
          productId: item.product?._id || item.product,
          productName: item.productName || item.product?.name || 'Unknown',
          product: item.product || {
            _id: item.product?._id || item.product,
            name: item.productName || item.product?.name,
            nameEn: item.product?.nameEn,
            department: item.department || item.product?.department,
            unit: item.unit || item.product?.unit,
            unitEn: item.product?.unitEn
          },
          quantity: item.quantity,
          status: item.status || 'pending',
          assignedTo: item.assignedTo,
          department: item.department || item.product?.department,
          unit: item.unit || item.product?.unit,
          startedAt: item.startedAt,
          completedAt: item.completedAt,
          rejectReason: item.rejectReason,
          notes: item.notes
        })),
        status: order.status,
        notes: order.notes || '',
        createdAt: order.createdAt,
        createdBy: order.createdBy,
        approvedBy: order.approvedBy,
        approvedAt: order.approvedAt,
        priority: order.priority || 'medium'
      }));
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    onError: (err) => {
      console.error('Factory orders query error:', err.message);
      toast.error(err.message || 'خطأ في جلب طلبات الإنتاج', { position: isRtl ? 'top-right' : 'top-left' });
    },
  });

  // Chef Assigned Items Query - مطابق للنقطة 10 في الراوتس
  const { data: assignedItemsData, refetch: refetchAssignedItems } = useQuery<ChefOrderItem[], Error>({
    queryKey: ['chefAssignedItems', user?.id, language],
    queryFn: async () => {
      if (!isChef || !user?.id) return [];
      
      const response = await factoryOrdersAPI.getAssignedItems();
      const data = response.data?.data || response?.data || [];
      if (!Array.isArray(data)) return [];
      
      return data.map((item: any) => ({
        ...item,
        _id: item._id,
        id: item._id,
        orderId: item.orderId || item.order?._id,
        orderNumber: item.orderNumber || item.order?.orderNumber,
        orderStatus: item.orderStatus || item.order?.status,
        orderCreatedAt: item.orderCreatedAt || item.order?.createdAt,
        productId: item.product?._id || item.product,
        productName: item.productName || item.product?.name || 'Unknown',
        product: item.product || {
          _id: item.product?._id || item.product,
          name: item.productName || item.product?.name,
          nameEn: item.product?.nameEn,
          department: item.department || item.product?.department,
          unit: item.unit || item.product?.unit,
          unitEn: item.product?.unitEn
        },
        quantity: item.quantity,
        status: item.status || 'pending',
        assignedTo: item.assignedTo,
        department: item.department || item.product?.department,
        unit: item.unit || item.product?.unit,
        startedAt: item.startedAt,
        completedAt: item.completedAt,
        rejectReason: item.rejectReason,
        notes: item.notes
      }));
    },
    enabled: isChef && !!user?.id,
    staleTime: 1 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    onError: (err) => {
      console.error('Assigned items query error:', err.message);
      toast.error(err.message || 'خطأ في جلب المهام المعينة', { position: isRtl ? 'top-right' : 'top-left' });
    },
  });

  // Inventory Query - معدل للشيفات
  const { data: inventoryData, isLoading: inventoryLoading, error: inventoryError } = useQuery<
    FactoryInventoryItem[],
    Error
  >({
    queryKey: ['factoryInventory', debouncedSearchQuery, filterStatus, filterDepartment, currentPage, language, userDepartments],
    queryFn: async () => {
      const params: any = {
        search: debouncedSearchQuery || undefined,
        department: filterDepartment || undefined,
        stockStatus: filterStatus || undefined,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        lang: language,
      };

      if (isChef && userDepartments.length > 0) {
        params.department = userDepartments.join(',');
      }

      const response = await factoryInventoryAPI.getAll(params);
      const data = response.data?.data || response?.data || [];
      if (!Array.isArray(data)) {
        console.warn('Invalid data format:', data);
        return [];
      }
      
      return data.map((item: any) => formatInventoryFromBackend(item, isRtl));
    },
    enabled: !!user?.role && ['production', 'admin', 'chef'].includes(user.role) && viewMode === 'inventory',
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    onError: (err) => {
      console.error('Inventory query error:', err.message);
      toast.error(err.message || t.errors.fetchInventory, { position: isRtl ? 'top-right' : 'top-left' });
    },
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
  });

  // Chefs Query
  const { data: chefsData, isLoading: chefsLoading, refetch: refetchChefs } = useQuery<Chef[], Error>({
    queryKey: ['factoryChefs', language],
    queryFn: async () => {
      const response = await chefsAPI.getAll();
      const data = Array.isArray(response) ? response : response?.data || response?.chefs || [];
      
      return data.map((chef: any) => ({
        _id: chef._id || chef.id,
        id: chef._id || chef.id,
        user: chef.user?._id || chef.userId || chef._id,
        userId: chef.user?._id || chef.userId || chef._id,
        displayName: isRtl
          ? (chef.user?.name || chef.name || 'شيف بدون اسم')
          : (chef.user?.nameEn || chef.nameEn || chef.user?.name || chef.name || 'Unnamed Chef'),
        name: chef.user?.name || chef.name,
        nameEn: chef.user?.nameEn || chef.nameEn,
        department: Array.isArray(chef.department)
          ? chef.department.map((d: any) => ({
              _id: d._id,
              name: d.name || (isRtl ? 'غير معروف' : 'Unknown'),
              nameEn: d.nameEn,
              displayName: isRtl ? d.name : (d.nameEn || d.name),
            }))
          : [],
        status: chef.status || 'active',
      }));
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
    onError: (err) => {
      console.error('Chefs query error:', err.message);
      toast.error(err.message || 'خطأ في جلب بيانات الشيفات', { position: isRtl ? 'top-right' : 'top-left' });
    },
  });

  // Product History Query
  const { data: productHistory, isLoading: historyLoading } = useQuery<ProductHistoryEntry[], Error>({
    queryKey: ['factoryProductHistory', selectedProductId, language],
    queryFn: async () => {
      if (!selectedProductId || !isValidObjectId(selectedProductId)) {
        throw new Error(t.errors.invalidProductId);
      }
      const response = await factoryInventoryAPI.getHistory({ 
        productId: selectedProductId, 
        lang: language 
      });
      const data = response.data?.data || response?.data || [];
      if (!Array.isArray(data)) {
        return [];
      }
      return data.map((item: any) => ({
        id: item._id,
        _id: item._id,
        product: item.product,
        action: item.action,
        displayAction: item.displayAction || item.action,
        quantity: item.quantity,
        reference: item.reference,
        referenceType: item.referenceType,
        referenceId: item.referenceId,
        createdBy: item.createdBy,
        createdAt: item.createdAt,
        notes: item.notes,
        isDamaged: item.isDamaged || false,
        previousStock: item.previousStock || 0,
        newStock: item.newStock || 0
      }));
    },
    enabled: isDetailsModalOpen && !!selectedProductId && isValidObjectId(selectedProductId),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    onError: (err) => {
      console.error('Product history query error:', err.message);
      toast.error(err.message || t.errors.productNotFound, { position: isRtl ? 'top-right' : 'top-left' });
    },
  });

  // جلب المنتجات المتاحة
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await productsAPI.getAll({ limit: 0, lang: language, isActive: true });
        const products = response.data || [];
        
        let filteredProducts = products
          .filter((product: any) => product && product._id && isValidObjectId(product._id) && product.isActive)
          .map((product: any) => ({
            productId: product._id,
            productName: isRtl ? product.name : product.nameEn || product.name,
            unit: isRtl ? (product.unit || t.unit) : product.unitEn || product.unit || 'N/A',
            departmentName: isRtl
              ? product.department?.name || t.allDepartments
              : product.department?.nameEn || product.department?.name || 'Unknown',
            departmentId: product.department?._id || '',
          }));
        
        if (isChef && userDepartments.length > 0) {
          filteredProducts = filteredProducts.filter((p) => 
            p.departmentId && userDepartments.includes(p.departmentId)
          );
        }
        
        setAvailableProducts(filteredProducts);
      } catch (err: any) {
        console.error('Error fetching products:', err.message);
        toast.error(err.message || t.errors.productNotFound, { position: isRtl ? 'top-right' : 'top-left' });
      }
    };
    
    fetchProducts();
  }, [isRtl, t, isChef, userDepartments, language]);

  // Socket Events
  useEffect(() => {
    if (!socket || !user?.role || !isConnected) return;
    
    const handleFactoryInventoryUpdated = ({ productId }: { productId: string }) => {
      if (!isValidObjectId(productId)) return;
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      if (selectedProductId === productId) {
        queryClient.invalidateQueries({ queryKey: ['factoryProductHistory'] });
      }
      toast.success(t.notifications.inventoryUpdated, { position: isRtl ? 'top-right' : 'top-left' });
    };

    const handleFactoryOrderCreated = ({ orderId }: { orderId: string }) => {
      if (!isValidObjectId(orderId)) return;
      toast.success(t.notifications.productionCreated, { position: isRtl ? 'top-right' : 'top-left' });
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
      if (isChef) {
        refetchAssignedItems();
      }
    };

    const handleFactoryTaskAssigned = ({ factoryOrderId }: { factoryOrderId: string }) => {
      if (!isValidObjectId(factoryOrderId)) return;
      toast.info(t.notifications.taskAssigned, { position: isRtl ? 'top-right' : 'top-left' });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
      if (isChef) {
        refetchAssignedItems();
      }
    };

    const handleFactoryOrderCompleted = ({ factoryOrderId }: { factoryOrderId: string }) => {
      if (!isValidObjectId(factoryOrderId)) return;
      toast.success(t.notifications.orderCompleted, { position: isRtl ? 'top-right' : 'top-left' });
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
    };

    const handleFactoryItemStatusChanged = ({ orderId, itemId }: { orderId: string, itemId: string }) => {
      if (!isValidObjectId(orderId) || !isValidObjectId(itemId)) return;
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      queryClient.invalidateQueries({ queryKey: ['chefAssignedItems'] });
      refetchFactoryOrders();
      refetchAssignedItems();
    };

    socket.on('inventoryUpdated', handleFactoryInventoryUpdated);
    socket.on('factoryOrderCreated', handleFactoryOrderCreated);
    socket.on('factoryTaskAssigned', handleFactoryTaskAssigned);
    socket.on('factoryOrderCompleted', handleFactoryOrderCompleted);
    socket.on('factoryItemStatusChanged', handleFactoryItemStatusChanged);

    const reconnectInterval = setInterval(() => {
      if (!isConnected && socket) {
        socket.connect();
      }
    }, 5000);

    return () => {
      socket.off('inventoryUpdated', handleFactoryInventoryUpdated);
      socket.off('factoryOrderCreated', handleFactoryOrderCreated);
      socket.off('factoryTaskAssigned', handleFactoryTaskAssigned);
      socket.off('factoryOrderCompleted', handleFactoryOrderCompleted);
      socket.off('factoryItemStatusChanged', handleFactoryItemStatusChanged);
      clearInterval(reconnectInterval);
    };
  }, [socket, user, isConnected, queryClient, addNotification, t, isRtl, selectedProductId, refetchFactoryOrders, refetchAssignedItems, isChef]);

  // Department options
  const departmentOptions = useMemo(() => {
    const options = [
      { value: '', label: t.allDepartments }
    ];
    
    if (isChef && userDepartments.length > 0) {
      const chefDepts = allDepartments.filter(dept => 
        userDepartments.includes(dept._id)
      );
      options.push(...chefDepts.map(dept => ({
        value: dept._id,
        label: dept.displayName
      })));
    } else {
      options.push(...allDepartments.map(dept => ({
        value: dept._id,
        label: dept.displayName
      })));
    }
    
    return options;
  }, [allDepartments, isChef, userDepartments, t]);

  // Status options
  const statusOptions = useMemo(
    () => [
      { value: '', label: t.allStatuses },
      { value: InventoryStatus.LOW, label: t.lowStock },
      { value: InventoryStatus.NORMAL, label: t.normal },
      { value: InventoryStatus.HIGH, label: t.high },
    ],
    [t]
  );

  // Order status options
  const orderStatusOptions = useMemo(
    () => [
      { value: '', label: t.allStatuses },
      { value: 'requested', label: isRtl ? 'مطلوب' : 'Requested' },
      { value: 'pending', label: isRtl ? 'قيد الانتظار' : 'Pending' },
      { value: 'approved', label: isRtl ? 'معتمد' : 'Approved' },
      { value: 'in_production', label: isRtl ? 'في الإنتاج' : 'In Production' },
      { value: 'completed', label: isRtl ? 'مكتمل' : 'Completed' },
      { value: 'stocked', label: isRtl ? 'مخزن' : 'Stocked' },
      { value: 'cancelled', label: isRtl ? 'ملغى' : 'Cancelled' },
    ],
    [t, isRtl]
  );

  // Product to department map
  const productDepartmentMap = useMemo(() => {
    const map: Record<string, string> = {};
    availableProducts.forEach((prod) => {
      if (prod.productId && prod.departmentId) {
        map[prod.productId] = prod.departmentId;
      }
    });
    return map;
  }, [availableProducts]);

  // Filtered and paginated inventory
  const filteredInventory = useMemo(
    () =>
      (inventoryData || []).filter(
        (item) =>
          item.product &&
          (!filterStatus || item.stockStatus === filterStatus) &&
          (!filterDepartment || item.product.department?._id === filterDepartment) &&
          (item.product.displayName.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
            item.product.code.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
            (item.product.department?.displayName?.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ?? false))
      ),
    [inventoryData, debouncedSearchQuery, filterStatus, filterDepartment]
  );
  
  const totalInventoryPages = Math.ceil((filteredInventory.length || 0) / ITEMS_PER_PAGE);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let orders = factoryOrdersData || [];
    
    if (orderFilterStatus) {
      orders = orders.filter(order => order.status === orderFilterStatus);
    }
    
    if (isChef && userDepartments.length > 0) {
      orders = orders.filter(order => {
        const isAssigned = order.items.some(item => 
          item.assignedTo?._id === user?.id
        );
        
        const hasProductsInDepartments = order.items.some(item => {
          const productDeptId = item.product?.department?._id || 
                               item.product?.department ||
                               item.department?._id;
          return productDeptId && userDepartments.includes(productDeptId);
        });
        
        return isAssigned || hasProductsInDepartments;
      });
    }
    
    return orders;
  }, [factoryOrdersData, orderFilterStatus, isChef, userDepartments, user]);

  // Pending orders for assignment
  const pendingOrdersForAssignment = useMemo(() => {
    return (factoryOrdersData || []).filter(
      order => order.status === 'approved' && order.items.some(item => !item.assignedTo && item.status !== 'rejected')
    );
  }, [factoryOrdersData]);

  // Completed orders ready for inventory
  const completedOrdersForInventory = useMemo(() => {
    return filteredOrders.filter(order => 
      order.status === 'in_production' && 
      order.items.every(item => item.status === 'completed' || item.status === 'rejected')
    );
  }, [filteredOrders]);

  // Statistics
  const inventoryStats = useMemo(() => {
    const totalInventory = filteredInventory.length;
    const lowStock = filteredInventory.filter(item => item.stockStatus === InventoryStatus.LOW).length;
    const inProduction = filteredInventory.filter(item => {
      return (factoryOrdersData || []).some(order => 
        (order.status === 'pending' || order.status === 'approved' || order.status === 'in_production') &&
        order.items.some(orderItem => orderItem.productId === item.product?._id)
      );
    }).length;
    
    return { totalInventory, lowStock, inProduction };
  }, [filteredInventory, factoryOrdersData]);

  const orderStats = useMemo(() => {
    const pendingOrders = filteredOrders.filter(order => 
      order.status === 'pending' || order.status === 'requested'
    ).length;
    
    const myOrders = isChef ? filteredOrders.filter(order => 
      order.items.some(item => item.assignedTo?._id === user?.id)
    ).length : 0;
    
    const myTasks = isChef ? (assignedItemsData || []).filter(item => 
      item.assignedTo?._id === user?.id && (item.status === 'assigned' || item.status === 'in_progress')
    ).length : 0;
    
    return { pendingOrders, myOrders, myTasks };
  }, [filteredOrders, assignedItemsData, isChef, user]);

  // Handlers
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleOpenProductionModal = useCallback((item?: FactoryInventoryItem) => {
    if (item && isChef) {
      const itemDeptId = item.product?.department?._id;
      if (itemDeptId && !userDepartments.includes(itemDeptId)) {
        toast.error(t.errors.productNotInDepartment, { position: isRtl ? 'top-right' : 'top-left' });
        return;
      }
    }
    
    if (isChef && userDepartments.length === 0) {
      toast.error(t.errors.chefNoDepartments, { position: isRtl ? 'top-right' : 'top-left' });
      return;
    }
    
    setPreselectedItem(item || null);
    setIsProductionModalOpen(true);
  }, [isChef, userDepartments, t, isRtl]);

  const handleCloseProductionModal = useCallback(() => {
    setIsProductionModalOpen(false);
    setPreselectedItem(null);
  }, []);

  const handleOpenEditModal = useCallback((item: FactoryInventoryItem) => {
    if (isChef) {
      const itemDeptId = item.product?.department?._id;
      if (itemDeptId && !userDepartments.includes(itemDeptId)) {
        toast.error(t.errors.unauthorizedAccess, { position: isRtl ? 'top-right' : 'top-left' });
        return;
      }
    }
    
    setSelectedItem(item);
    setEditForm({ minStockLevel: item.minStockLevel, maxStockLevel: item.maxStockLevel });
    setEditErrors({});
    setIsEditModalOpen(true);
  }, [isChef, userDepartments, t, isRtl]);

  const handleOpenDetailsModal = useCallback((item: FactoryInventoryItem) => {
    if (isChef) {
      const itemDeptId = item.product?.department?._id;
      if (itemDeptId && !userDepartments.includes(itemDeptId)) {
        toast.error(t.errors.unauthorizedAccess, { position: isRtl ? 'top-right' : 'top-left' });
        return;
      }
    }
    
    if (item.product && isValidObjectId(item.product._id)) {
      setSelectedProductId(item.product._id);
      setIsDetailsModalOpen(true);
    } else {
      toast.error(t.errors.invalidProductId, { position: isRtl ? 'top-right' : 'top-left' });
    }
  }, [isChef, userDepartments, t, isRtl]);

  const handleOpenOrderDetails = useCallback((order: FactoryOrder) => {
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  }, []);

  // Handle opening assign chefs modal
  const handleOpenAssignModal = useCallback((order: FactoryOrder) => {
    if (order.status !== 'approved') {
      toast.error(isRtl ? 'الطلب لم يتم الموافقة عليه' : 'Order not approved', {
        position: isRtl ? 'top-right' : 'top-left',
      });
      return;
    }

    setSelectedOrder(order);
    setAssignFormData({
      items: order.items
        .filter(item => !item.assignedTo && item.status !== 'rejected')
        .map(item => ({
          itemId: item._id,
          assignedTo: '',
          product: item.productName || item.product?.name || 'Unknown',
          quantity: item.quantity,
          unit: item.unit || item.product?.unit || 'unit',
          rejected: false,
          rejectReason: '',
        })),
    });
    setIsAssignModalOpen(true);
  }, [isRtl]);

  // Handle assign chefs submission
  const handleAssignChefs = useCallback(async (orderId: string) => {
    if (!user?.id || assignFormData.items.some(item => !item.assignedTo && !item.rejected)) {
      toast.error(isRtl ? 'يرجى تعيين شيف أو رفض لكل عنصر' : 'Please assign chef or reject each item', {
        position: isRtl ? 'top-right' : 'top-left',
      });
      return;
    }

    setSubmitting(orderId);
    setError('');

    try {
      const itemsToAssign = assignFormData.items.map(item => ({
        itemId: item.itemId,
        assignedTo: item.rejected ? null : item.assignedTo,
        rejected: item.rejected,
        rejectReason: item.rejectReason || '',
      }));

      await factoryOrdersAPI.assignChefs(orderId, { items: itemsToAssign });
      
      toast.success(isRtl ? 'تم تعيين الشيفات بنجاح' : 'Chefs assigned successfully', {
        position: isRtl ? 'top-right' : 'top-left',
      });

      if (socket && isConnected) {
        socket.emit('factoryTaskAssigned', { 
          orderId, 
          items: itemsToAssign 
        });
      }

      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
      refetchChefs();
      refetchAssignedItems();

      setIsAssignModalOpen(false);
      setSelectedOrder(null);
      setAssignFormData({ items: [] });
      
    } catch (err: any) {
      console.error('Assign chefs error:', err.message);
      setError(err.message || (isRtl ? 'خطأ في تعيين الشيفات' : 'Error assigning chefs'));
      toast.error(err.message || (isRtl ? 'خطأ في تعيين الشيفات' : 'Error assigning chefs'), {
        position: isRtl ? 'top-right' : 'top-left',
      });
    } finally {
      setSubmitting(null);
    }
  }, [user, assignFormData, socket, isConnected, isRtl, queryClient, refetchFactoryOrders, refetchChefs, refetchAssignedItems]);

  // Handle approve order
  const handleApproveOrder = useCallback(async (orderId: string) => {
    try {
      await factoryOrdersAPI.approve(orderId);
      toast.success(t.notifications.orderApproved, { position: isRtl ? 'top-right' : 'top-left' });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
    } catch (err: any) {
      console.error('Approve order error:', err.message);
      toast.error(err.message || (isRtl ? 'خطأ في الموافقة على الطلب' : 'Error approving order'), {
        position: isRtl ? 'top-right' : 'top-left',
      });
    }
  }, [t, isRtl, queryClient, refetchFactoryOrders]);

  // Handle start item production (Chef)
  const handleStartItem = useCallback(async (orderId: string, itemId: string) => {
    try {
      setSubmitting(`start-${itemId}`);
      await factoryOrdersAPI.startItem(orderId, itemId);
      
      toast.success(t.notifications.itemStarted, { position: isRtl ? 'top-right' : 'top-left' });
      
      if (socket && isConnected) {
        socket.emit('factoryItemStatusChanged', { 
          orderId, 
          itemId,
          status: 'in_progress'
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      queryClient.invalidateQueries({ queryKey: ['chefAssignedItems'] });
      refetchFactoryOrders();
      refetchAssignedItems();
      
    } catch (err: any) {
      console.error('Start item error:', err.message);
      toast.error(err.message || (isRtl ? 'خطأ في بدء إنتاج العنصر' : 'Error starting item'), {
        position: isRtl ? 'top-right' : 'top-left',
      });
    } finally {
      setSubmitting(null);
    }
  }, [t, isRtl, socket, isConnected, queryClient, refetchFactoryOrders, refetchAssignedItems]);

  // Handle complete item (Chef)
  const handleCompleteItem = useCallback(async (orderId: string, itemId: string, notes?: string) => {
    try {
      setSubmitting(`complete-${itemId}`);
      await factoryOrdersAPI.completeItem(orderId, itemId, { notes });
      
      toast.success(t.notifications.itemCompleted, { position: isRtl ? 'top-right' : 'top-left' });
      
      if (socket && isConnected) {
        socket.emit('factoryItemStatusChanged', { 
          orderId, 
          itemId,
          status: 'completed'
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      queryClient.invalidateQueries({ queryKey: ['chefAssignedItems'] });
      refetchFactoryOrders();
      refetchAssignedItems();
      
    } catch (err: any) {
      console.error('Complete item error:', err.message);
      toast.error(err.message || (isRtl ? 'خطأ في إكمال العنصر' : 'Error completing item'), {
        position: isRtl ? 'top-right' : 'top-left',
      });
    } finally {
      setSubmitting(null);
    }
  }, [t, isRtl, socket, isConnected, queryClient, refetchFactoryOrders, refetchAssignedItems]);

  // Handle reject item (Chef)
  const handleRejectItem = useCallback(async (orderId: string, itemId: string, rejectReason: string) => {
    try {
      setSubmitting(`reject-${itemId}`);
      await factoryOrdersAPI.rejectItem(orderId, itemId, { rejectReason });
      
      toast.success(t.notifications.itemRejected, { position: isRtl ? 'top-right' : 'top-left' });
      
      if (socket && isConnected) {
        socket.emit('factoryItemStatusChanged', { 
          orderId, 
          itemId,
          status: 'rejected'
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      queryClient.invalidateQueries({ queryKey: ['chefAssignedItems'] });
      refetchFactoryOrders();
      refetchAssignedItems();
      
    } catch (err: any) {
      console.error('Reject item error:', err.message);
      toast.error(err.message || (isRtl ? 'خطأ في رفض العنصر' : 'Error rejecting item'), {
        position: isRtl ? 'top-right' : 'top-left',
      });
    } finally {
      setSubmitting(null);
    }
  }, [t, isRtl, socket, isConnected, queryClient, refetchFactoryOrders, refetchAssignedItems]);

  // Handle complete order to inventory
  const handleConfirmProduction = useCallback(async (orderId: string) => {
    if (!confirm(isRtl 
      ? 'هل تريد تأكيد الإتمام ونقل المنتجات إلى المخزون؟' 
      : 'Do you want to confirm completion and move products to inventory?'
    )) return;

    try {
      setSubmitting(`confirm-${orderId}`);
      await factoryOrdersAPI.confirmProduction(orderId);
      
      toast.success(t.notifications.orderMovedToInventory, { 
        position: isRtl ? 'top-right' : 'top-left' 
      });
      
      if (socket && isConnected) {
        socket.emit('factoryOrderCompleted', { 
          orderId
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      refetchFactoryOrders();
      
    } catch (err: any) {
      console.error('Confirm production error:', err.message);
      toast.error(err.message || (isRtl 
        ? 'خطأ في تأكيد الإنتاج' 
        : 'Error confirming production'
      ), { position: isRtl ? 'top-right' : 'top-left' });
    } finally {
      setSubmitting(null);
    }
  }, [isRtl, socket, isConnected, queryClient, refetchFactoryOrders, t]);

  // Handle cancel order
  const handleCancelOrder = useCallback(async (orderId: string, notes?: string) => {
    if (!confirm(isRtl 
      ? 'هل تريد إلغاء هذا الطلب؟' 
      : 'Do you want to cancel this order?'
    )) return;

    try {
      setSubmitting(`cancel-${orderId}`);
      await factoryOrdersAPI.cancelOrder(orderId, { notes });
      
      toast.success(t.notifications.orderCancelled, { 
        position: isRtl ? 'top-right' : 'top-left' 
      });
      
      if (socket && isConnected) {
        socket.emit('factoryOrderCancelled', { 
          orderId
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      queryClient.invalidateQueries({ queryKey: ['chefAssignedItems'] });
      refetchFactoryOrders();
      refetchAssignedItems();
      
    } catch (err: any) {
      console.error('Cancel order error:', err.message);
      toast.error(err.message || (isRtl 
        ? 'خطأ في إلغاء الطلب' 
        : 'Error cancelling order'
      ), { position: isRtl ? 'top-right' : 'top-left' });
    } finally {
      setSubmitting(null);
    }
  }, [isRtl, socket, isConnected, queryClient, refetchFactoryOrders, refetchAssignedItems, t]);

  // Validate edit form
  const validateEditForm = useCallback(() => {
    const errors: Record<string, string> = {};
    if (editForm.minStockLevel < 0) {
      errors.minStockLevel = t.errors.nonNegative.replace('{field}', t.minStock);
    }
    if (editForm.maxStockLevel < 0) {
      errors.maxStockLevel = t.errors.nonNegative.replace('{field}', t.maxStock);
    }
    if (editForm.maxStockLevel <= editForm.minStockLevel) {
      errors.maxStockLevel = t.errors.maxGreaterMin;
    }
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  }, [editForm, t]);

  // Create production mutation
  const createProductionMutation = useMutation<{ orderId: string; orderNumber: string }, Error, { productionForm: ProductionFormState; user: any; socket: any; isConnected: boolean; isRtl: boolean; t: any }>({
    mutationFn: async ({ productionForm, user }) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      
      if (isChef) {
        productionForm.items.forEach(item => {
          const deptId = productDepartmentMap[item.product];
          if (!deptId || !userDepartments.includes(deptId)) {
            throw new Error(t.errors.productNotInDepartment);
          }
        });
      }
      
      const aggregatedItems: Record<string, ProductionItem> = {};
      productionForm.items.forEach(item => {
        const key = `${item.product}_${item.assignedTo}`;
        if (!aggregatedItems[key]) {
          aggregatedItems[key] = {
            product: item.product,
            quantity: 0,
            assignedTo: item.assignedTo,
          };
        }
        aggregatedItems[key].quantity += item.quantity;
      });

      const items = Object.values(aggregatedItems).filter(
        item => item.product && isValidObjectId(item.product) && item.quantity > 0
      );

      if (items.length === 0) {
        throw new Error(t.errors.noItemSelected);
      }

      const data = {
        orderNumber: productionForm.orderNumber,
        items: items.map(item => ({
          product: item.product,
          quantity: item.quantity,
          assignedTo: item.assignedTo || undefined,
        })),
        notes: productionForm.notes || '',
        priority: productionForm.priority || 'medium',
      };

      const response = await factoryOrdersAPI.create(data);
      
      return {
        orderId: response.data?._id || crypto.randomUUID(),
        orderNumber: response.data?.orderNumber || data.orderNumber,
      };
    },
    onSuccess: (data, variables) => {
      const { socket, isConnected, isRtl, t } = variables;
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      queryClient.invalidateQueries({ queryKey: ['factoryOrders'] });
      toast.success(t.notifications.productionCreated, { position: isRtl ? 'top-right' : 'top-left' });
      
      if (socket && isConnected) {
        socket.emit('factoryOrderCreated', {
          orderId: data.orderId,
          orderNumber: data.orderNumber,
          eventId: crypto.randomUUID(),
        });
      }
    },
    onError: (err: any, variables) => {
      const { t, isRtl } = variables;
      console.error('Create production order error:', err.message);
      let errorMessage = err.message || t.errors.createProduction;
      toast.error(errorMessage, { position: isRtl ? 'top-right' : 'top-left' });
    },
  });

  // Update inventory mutation
  const updateInventoryMutation = useMutation<void, Error, void>({
    mutationFn: async () => {
      if (!validateEditForm()) {
        throw new Error(t.errors.invalidForm);
      }
      if (!selectedItem || !isValidObjectId(selectedItem._id)) {
        throw new Error(t.errors.noItemSelected);
      }
      
      if (isChef) {
        const itemDeptId = selectedItem.product?.department?._id;
        if (itemDeptId && !userDepartments.includes(itemDeptId)) {
          throw new Error(t.errors.unauthorizedAccess);
        }
      }
      
      await factoryInventoryAPI.updateStock(selectedItem._id, {
        minStockLevel: editForm.minStockLevel,
        maxStockLevel: editForm.maxStockLevel,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['factoryInventory'] });
      setIsEditModalOpen(false);
      setEditForm({ minStockLevel: 0, maxStockLevel: 0 });
      setEditErrors({});
      setSelectedItem(null);
      toast.success(t.notifications.inventoryUpdated, { position: isRtl ? 'top-right' : 'top-left' });
      
      if (socket && isConnected && selectedItem?.product?._id) {
        socket.emit('inventoryUpdated', {
          productId: selectedItem.product._id,
          eventId: crypto.randomUUID(),
        });
      }
    },
    onError: (err) => {
      console.error('Update inventory error:', err.message);
      const errorMessage = err.message || t.errors.updateInventory;
      toast.error(errorMessage, { position: isRtl ? 'top-right' : 'top-left' });
      setEditErrors({ form: errorMessage });
    },
  });

  const errorMessage = inventoryError?.message || '';

  return (
    <div className="mx-auto px-4 py-4 max-w-7xl">
      {/* Header */}
      <div className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div className="flex items-center gap-3">
          <Package className="w-7 h-7 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
            <p className="text-gray-600 text-sm">{t.description}</p>
            {isChef && userDepartments.length > 0 && (
              <p className="text-sm text-amber-600 mt-1">
                {isRtl ? 'أقسامك: ' : 'Your departments: '}
                {userDepartments.map(deptId => {
                  const dept = allDepartments.find(d => d._id === deptId);
                  return dept?.displayName || deptId;
                }).join(', ')}
              </p>
            )}
            {isChef && userDepartments.length === 0 && (
              <p className="text-sm text-red-600 mt-1">
                {t.errors.chefNoDepartments}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('inventory')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'inventory' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <Grid className="w-4 h-4 inline-block mr-2" />
              {isRtl ? 'المخزون' : 'Inventory'}
            </button>
            <button
              onClick={() => setViewMode('orders')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'orders' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <List className="w-4 h-4 inline-block mr-2" />
              {isRtl ? 'الطلبات' : 'Orders'}
            </button>
            {isChef && (
              <button
                onClick={() => setViewMode('chef')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'chef' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <ChefHat className="w-4 h-4 inline-block mr-2" />
                {isRtl ? 'مهامي' : 'My Tasks'}
              </button>
            )}
          </div>

          {viewMode === 'orders' && (
            <div className="flex flex-wrap gap-2">
              {pendingOrdersForAssignment.length > 0 && ['admin', 'production'].includes(user?.role) && (
                <button
                  onClick={() => {
                    if (pendingOrdersForAssignment.length > 0) {
                      handleOpenAssignModal(pendingOrdersForAssignment[0]);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  aria-label={t.assignChefs}
                >
                  <Users className="w-4 h-4" />
                  {t.assignChefs} ({pendingOrdersForAssignment.length})
                </button>
              )}
              
              {completedOrdersForInventory.length > 0 && ['admin', 'production'].includes(user?.role) && (
                <button
                  onClick={() => {
                    if (completedOrdersForInventory.length > 0) {
                      handleConfirmProduction(completedOrdersForInventory[0]._id);
                    }
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
                  disabled={submitting?.startsWith('confirm-')}
                >
                  {submitting?.startsWith('confirm-') ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCheck className="w-4 h-4" />
                  )}
                  {t.confirmProduction} ({completedOrdersForInventory.length})
                </button>
              )}
            </div>
          )}
          
          <button
            onClick={() => handleOpenProductionModal()}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
            disabled={isChef && userDepartments.length === 0}
            aria-label={t.create}
          >
            <Plus className="w-4 h-4" />
            {t.create}
          </button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className={`grid gap-4 mb-6 ${
        isChef ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
      }`}>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t.stats.totalInventory}</p>
              <p className="text-2xl font-bold text-gray-900">{inventoryStats.totalInventory}</p>
            </div>
            <Package className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t.stats.lowStock}</p>
              <p className="text-2xl font-bold text-gray-900">{inventoryStats.lowStock}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t.stats.inProduction}</p>
              <p className="text-2xl font-bold text-gray-900">{inventoryStats.inProduction}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{isChef ? t.myOrders : t.stats.pendingOrders}</p>
              <p className="text-2xl font-bold text-gray-900">
                {isChef ? orderStats.myOrders : orderStats.pendingOrders}
              </p>
            </div>
            <Clock className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        {isChef && (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t.stats.myTasks}</p>
                <p className="text-2xl font-bold text-gray-900">{orderStats.myTasks}</p>
              </div>
              <ChefHat className="w-8 h-8 text-amber-500" />
            </div>
          </div>
        )}
      </div>
      
      {errorMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3"
        >
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-600 text-sm font-medium">{errorMessage}</span>
        </motion.div>
      )}
      
      {/* Pending Orders Notification */}
      {viewMode === 'inventory' && pendingOrdersForAssignment.length > 0 && ['admin', 'production'].includes(user?.role) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <ChefHat className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-blue-800 font-medium">
                {isRtl ? `لديك ${pendingOrdersForAssignment.length} طلب يحتاج تعيين شيفات` : 
                        `You have ${pendingOrdersForAssignment.length} order(s) needing chef assignment`}
              </p>
              <p className="text-blue-600 text-sm">
                {isRtl ? 'انقر على زر "تعيين الشيفات" لبدء التعيين' : 
                        'Click the "Assign Chefs" button to start assignment'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setViewMode('orders')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
          >
            {isRtl ? 'عرض الطلبات' : 'View Orders'}
          </button>
        </motion.div>
      )}
      
      {/* Filters */}
      <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        {viewMode === 'inventory' ? (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-1/2">
              <ProductSearchInput
                value={searchInput}
                onChange={handleSearchChange}
                placeholder={t.search}
                ariaLabel={t.search}
              />
            </div>
            <div className="w-full lg:w-1/2 flex flex-col sm:flex-row gap-4">
              <ProductDropdown
                value={filterStatus}
                onChange={(value) => {
                  setFilterStatus(value as InventoryStatus | '');
                  setCurrentPage(1);
                }}
                options={statusOptions}
                ariaLabel={t.filterByStatus}
              />
              <ProductDropdown
                value={filterDepartment}
                onChange={(value) => {
                  setFilterDepartment(value);
                  setCurrentPage(1);
                }}
                options={departmentOptions}
                ariaLabel={t.filterByDepartment}
              />
            </div>
          </div>
        ) : viewMode === 'orders' ? (
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="w-full lg:w-1/2">
              <ProductSearchInput
                value={searchInput}
                onChange={handleSearchChange}
                placeholder={isRtl ? 'البحث في الطلبات...' : 'Search orders...'}
                ariaLabel={isRtl ? 'البحث في الطلبات' : 'Search orders'}
              />
            </div>
            <div className="w-full lg:w-1/2 flex flex-col sm:flex-row gap-4">
              <ProductDropdown
                value={orderFilterStatus}
                onChange={(value) => {
                  setOrderFilterStatus(value);
                }}
                options={orderStatusOptions}
                ariaLabel={t.filterByStatus}
              />
              <button
                onClick={() => refetchFactoryOrders()}
                className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {isRtl ? 'تحديث' : 'Refresh'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {isRtl ? 'مهامي المعينة' : 'My Assigned Tasks'}
            </h3>
            <button
              onClick={() => refetchAssignedItems()}
              className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              {isRtl ? 'تحديث المهام' : 'Refresh Tasks'}
            </button>
          </div>
        )}
        <div className="mt-4 text-center text-sm text-gray-600 font-medium">
          {viewMode === 'inventory' 
            ? (isRtl ? `عدد العناصر: ${filteredInventory.length}` : `Items Count: ${filteredInventory.length}`)
            : viewMode === 'orders'
            ? (isRtl ? `عدد الطلبات: ${filteredOrders.length}` : `Orders Count: ${filteredOrders.length}`)
            : (isRtl ? `عدد المهام: ${(assignedItemsData || []).length}` : `Tasks Count: ${(assignedItemsData || []).length}`)
          }
        </div>
      </div>
      
      {/* Content based on view mode */}
      {viewMode === 'inventory' ? (
        <>
          {inventoryLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                  <div className="space-y-3 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                    <div className="mt-4 flex justify-end">
                      <div className="h-8 bg-gray-200 rounded-lg w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredInventory.length === 0 ? (
            <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-sm font-medium">{t.noItems}</p>
              {isChef && userDepartments.length === 0 ? (
                <p className="text-red-600 text-sm mt-2">{t.errors.chefNoDepartments}</p>
              ) : (
                <button
                  onClick={() => handleOpenProductionModal()}
                  className="mt-4 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                  aria-label={t.create}
                >
                  {t.create}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {filteredInventory.map((item) =>
                  item.product ? (
                    <motion.div
                      key={item._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-amber-200"
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <h3 className="font-bold text-gray-900 text-base truncate" style={{ fontWeight: 700 }}>
                            {item.product.displayName}
                          </h3>
                          <p className="text-sm text-gray-500">{item.product.code}</p>
                        </div>
                        <p className="text-sm text-amber-600">
                          {t.filterByDepartment}: {item.product.department?.displayName || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t.stock}: {item.currentStock}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t.minStock}: {item.minStockLevel}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t.maxStock}: {item.maxStockLevel}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t.unit}: {item.product.displayUnit}
                        </p>
                        <p
                          className={`text-sm font-medium ${
                            item.stockStatus === InventoryStatus.LOW
                              ? 'text-red-600'
                              : item.stockStatus === InventoryStatus.HIGH
                              ? 'text-yellow-600'
                              : 'text-green-600'
                          }`}
                        >
                          {item.displayStockStatus}
                        </p>
                      </div>
                      <div className="mt-4 flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenDetailsModal(item)}
                          className="p-2 text-green-600 hover:text-green-800 rounded-lg text-sm transition-colors duration-200"
                          aria-label={t.viewDetails}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {['admin', 'production'].includes(user?.role) && (
                          <>
                            <button
                              onClick={() => handleOpenEditModal(item)}
                              className="p-2 text-blue-600 hover:text-blue-800 rounded-lg text-sm transition-colors duration-200"
                              aria-label={t.editStockLimits}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleOpenProductionModal(item)}
                              className="p-2 text-amber-600 hover:text-amber-800 rounded-lg text-sm transition-colors duration-200"
                              aria-label={t.create}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {isChef && (
                          <button
                            onClick={() => handleOpenProductionModal(item)}
                            className="p-2 text-amber-600 hover:text-amber-800 rounded-lg text-sm transition-colors duration-200"
                            aria-label={t.create}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ) : null
                )}
              </AnimatePresence>
            </div>
          )}
          
          {totalInventoryPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
                disabled={currentPage === 1}
                aria-label={isRtl ? 'الصفحة السابقة' : 'Previous page'}
              >
                {isRtl ? 'السابق' : 'Previous'}
              </button>
              <span className="text-gray-700 font-medium">
                {isRtl ? `الصفحة ${currentPage} من ${totalInventoryPages}` : `Page ${currentPage} of ${totalInventoryPages}`}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(currentPage + 1, totalInventoryPages))}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
                disabled={currentPage === totalInventoryPages}
                aria-label={isRtl ? 'الصفحة التالية' : 'Next page'}
              >
                {isRtl ? 'التالي' : 'Next'}
              </button>
            </div>
          )}
        </>
      ) : viewMode === 'orders' ? (
        <>
          {/* Orders View */}
          <div className="space-y-4">
            {filteredOrders.length === 0 ? (
              <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-100">
                <List className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-sm font-medium">
                  {isRtl ? 'لا توجد طلبات' : 'No orders found'}
                </p>
                {isChef && userDepartments.length === 0 && (
                  <p className="text-red-600 text-sm mt-2">{t.errors.chefNoDepartments}</p>
                )}
              </div>
            ) : (
              filteredOrders.map((order) => (
                <motion.div
                  key={order._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-bold text-gray-900">{order.orderNumber}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          order.status === 'completed' || order.status === 'approved' || order.status === 'stocked' 
                            ? 'bg-green-100 text-green-800' 
                            : order.status === 'in_production'
                            ? 'bg-blue-100 text-blue-800'
                            : order.status === 'pending' || order.status === 'requested'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {order.status === 'requested' ? (isRtl ? 'مطلوب' : 'Requested') :
                           order.status === 'pending' ? (isRtl ? 'قيد الانتظار' : 'Pending') :
                           order.status === 'approved' ? (isRtl ? 'معتمد' : 'Approved') :
                           order.status === 'in_production' ? (isRtl ? 'في الإنتاج' : 'In Production') :
                           order.status === 'completed' ? (isRtl ? 'مكتمل' : 'Completed') :
                           order.status === 'stocked' ? (isRtl ? 'مخزن' : 'Stocked') :
                           (isRtl ? 'ملغى' : 'Cancelled')}
                        </span>
                        {order.priority && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            order.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            order.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {order.priority === 'urgent' ? (isRtl ? 'عاجل' : 'Urgent') :
                             order.priority === 'high' ? (isRtl ? 'عالية' : 'High') :
                             order.priority === 'medium' ? (isRtl ? 'متوسطة' : 'Medium') :
                             (isRtl ? 'منخفضة' : 'Low')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {isRtl ? 'تاريخ الإنشاء' : 'Created At'}: {new Date(order.createdAt).toLocaleDateString(isRtl ? 'ar-SA' : 'en-US')}
                      </p>
                      <p className="text-sm text-gray-600">
                        {isRtl ? 'بواسطة' : 'By'}: {order.createdBy?.displayName || order.createdBy?.name || 'Unknown'}
                      </p>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => handleOpenOrderDetails(order)}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        {t.viewDetails}
                      </button>
                      
                      {order.status === 'approved' && order.items.some(item => !item.assignedTo && item.status !== 'rejected') && ['admin', 'production'].includes(user?.role) && (
                        <button
                          onClick={() => handleOpenAssignModal(order)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          {t.assignChefs}
                        </button>
                      )}
                      
                      {order.status === 'requested' && ['admin', 'production'].includes(user?.role) && (
                        <button
                          onClick={() => handleApproveOrder(order._id)}
                          className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors duration-200"
                        >
                          {t.approveOrder}
                        </button>
                      )}
                      
                      {order.status === 'in_production' && order.items.every(item => item.status === 'completed' || item.status === 'rejected') && ['admin', 'production'].includes(user?.role) && (
                        <button
                          onClick={() => handleConfirmProduction(order._id)}
                          disabled={submitting === `confirm-${order._id}`}
                          className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 flex items-center gap-1"
                        >
                          {submitting === `confirm-${order._id}` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <CheckCheck className="w-3 h-3" />
                          )}
                          {t.confirmProduction}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {order.items.slice(0, 3).map((item, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">
                            {isRtl ? item.product?.name : item.product?.nameEn || item.product?.name}
                          </span>
                          <span className="text-gray-600">
                            ({item.quantity} {item.unit || (isRtl ? 'وحدة' : 'unit')})
                          </span>
                          {item.rejectReason && (
                            <span className="text-red-600 text-xs" title={item.rejectReason}>
                              [مرفوض]
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.assignedTo && (
                            <span className="text-gray-600">
                              {isRtl ? 'إلى' : 'To'}: {item.assignedTo.displayName || item.assignedTo.name}
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded text-xs ${
                            item.status === 'completed' ? 'bg-green-100 text-green-800' :
                            item.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                            item.status === 'assigned' ? 'bg-purple-100 text-purple-800' :
                            item.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {item.status === 'pending' ? (isRtl ? 'قيد الانتظار' : 'Pending') :
                             item.status === 'assigned' ? (isRtl ? 'معين' : 'Assigned') :
                             item.status === 'in_progress' ? (isRtl ? 'قيد التنفيذ' : 'In Progress') :
                             item.status === 'rejected' ? (isRtl ? 'مرفوض' : 'Rejected') :
                             (isRtl ? 'مكتمل' : 'Completed')}
                          </span>
                        </div>
                      </div>
                    ))}
                    
                    {order.items.length > 3 && (
                      <p className="text-sm text-gray-600 text-center">
                        + {order.items.length - 3} {isRtl ? 'عناصر أخرى' : 'more items'}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </>
      ) : (
        <>
          {/* Chef View */}
          <ChefOrdersView
            orders={filteredOrders}
            assignedItems={assignedItemsData || []}
            isRtl={isRtl}
            t={t}
            user={user}
            onViewOrderDetails={handleOpenOrderDetails}
            onStartItem={handleStartItem}
            onCompleteItem={handleCompleteItem}
            onRejectItem={handleRejectItem}
          />
        </>
      )}
      
      {/* Modals */}
      <ProductionOrderForm
        isOpen={isProductionModalOpen}
        onClose={handleCloseProductionModal}
        preselectedItem={preselectedItem}
        availableProducts={availableProducts}
        chefsData={chefsData || []}
        isChef={isChef}
        user={{ ...user, chefDepartments: userDepartments }}
        productDepartmentMap={productDepartmentMap}
        createProductionMutation={createProductionMutation}
        t={t}
        isRtl={isRtl}
        socket={socket}
        isConnected={isConnected}
      />
      
      {/* Assign Chefs Modal */}
      <AssignChefsModal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedOrder(null);
          setAssignFormData({ items: [] });
          setError('');
        }}
        selectedOrder={selectedOrder}
        chefs={chefsData || []}
        assignFormData={assignFormData}
        setAssignForm={setAssignFormData}
        assignChefs={handleAssignChefs}
        error={error}
        submitting={submitting}
        isRtl={isRtl}
        t={t}
      />
      
      {/* Order Details Modal */}
      <OrderDetailsModal
        isOpen={isOrderDetailsOpen}
        onClose={() => {
          setIsOrderDetailsOpen(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        isRtl={isRtl}
        t={t}
        user={user}
        onApproveOrder={handleApproveOrder}
        onUpdateItemStatus={(orderId, itemId, status, notes, rejectReason) => {
          if (status === 'completed') {
            handleCompleteItem(orderId, itemId, notes);
          } else if (status === 'rejected') {
            handleRejectItem(orderId, itemId, rejectReason || '');
          } else if (status === 'in_progress') {
            handleStartItem(orderId, itemId);
          }
        }}
        onCompleteToInventory={handleConfirmProduction}
        onCancelOrder={handleCancelOrder}
        onStartItem={handleStartItem}
      />
      
      {/* Edit Modal */}
      {isEditModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}
          role="dialog"
          aria-modal="true"
          aria-label={t.editStockLimits}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t.editStockLimits}</h2>
              <button
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditErrors({});
                  setSelectedItem(null);
                }}
                className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors duration-200"
                aria-label={t.cancel}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              {selectedItem?.product && (
                <p className="text-sm text-gray-600">
                  {t.productDetails}: {isRtl ? selectedItem.product.name : selectedItem.product.nameEn}
                </p>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.minStock}</label>
                <input
                  type="number"
                  value={editForm.minStockLevel}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setEditForm((prev) => ({ ...prev, minStockLevel: isNaN(value) ? 0 : value }));
                    setEditErrors((prev) => ({ ...prev, minStockLevel: undefined }));
                  }}
                  min={0}
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  aria-label={t.minStock}
                />
                {editErrors.minStockLevel && (
                  <p className="text-red-600 text-xs mt-1">{editErrors.minStockLevel}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t.maxStock}</label>
                <input
                  type="number"
                  value={editForm.maxStockLevel}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setEditForm((prev) => ({ ...prev, maxStockLevel: isNaN(value) ? 0 : value }));
                    setEditErrors((prev) => ({ ...prev, maxStockLevel: undefined }));
                  }}
                  min={0}
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm transition-all duration-200"
                  aria-label={t.maxStock}
                />
                {editErrors.maxStockLevel && (
                  <p className="text-red-600 text-xs mt-1">{editErrors.maxStockLevel}</p>
                )}
              </div>
              {editErrors.form && <p className="text-red-600 text-xs">{editErrors.form}</p>}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setEditErrors({});
                    setSelectedItem(null);
                  }}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-sm font-medium transition-colors duration-200"
                  aria-label={t.cancel}
                >
                  {t.cancel}
                </button>
                <button
                  onClick={() => updateInventoryMutation.mutate()}
                  disabled={updateInventoryMutation.isLoading}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors duration-200 disabled:opacity-50"
                  aria-label={updateInventoryMutation.isLoading ? t.saving : t.save}
                >
                  {updateInventoryMutation.isLoading ? t.saving : t.save}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      
      {/* Details Modal */}
      {isDetailsModalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4`}
          role="dialog"
          aria-modal="true"
          aria-label={t.productDetails}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-md sm:max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{t.productDetails}</h2>
              <button
                onClick={() => {
                  setIsDetailsModalOpen(false);
                  setSelectedProductId('');
                }}
                className="p-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors duration-200"
                aria-label={t.cancel}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {historyLoading ? (
              <div className="space-y-3 animate-pulse">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-4 bg-gray-200 rounded w-full"></div>
                ))}
              </div>
            ) : productHistory && productHistory.length > 0 ? (
              <div className="space-y-4">
                {productHistory.map((entry) => (
                  <div key={entry._id} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-sm text-gray-600">
                      {t.date}: {new Date(entry.createdAt).toLocaleDateString(language)}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t.type}: {entry.displayAction}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t.quantity}: {entry.quantity}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t.reference}: {entry.reference}
                    </p>
                    {entry.notes && (
                      <p className="text-sm text-gray-600">
                        {t.notes}: {entry.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-sm">{t.noHistory}</p>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default FactoryInventory;